import { FlatRateCoverage, PositionsBestandteil, ServiceItem, ServiceTransaction, ServiceTransferOrigin, ReportMetadata } from '../types';
import { altCoveredQuantities } from './externalAdvances';
import { isFlatRateService, isInspectionProofEntry, isOwnReportProofEntry, resolveFlatCoverage, type FlatCoverageServiceLike } from './flatRateLogic';
import { contractedQuantity } from './nepPosition';
import { agreedMinHours, applyMinHoursInput, bookableQuantities, defaultHoursForService, initialBookingAmountText, isHourlyUnit, readMinHoursInput } from './serviceUnits';
import { getServiceLog } from './serviceJournal';
import { childBookingDescription, childBookingRef, isMainInspectionBooking, ohneBuchungsart, reportNumberOf } from './serviceLogUtils';
import { bestandteilGate, hatBestandteile, istAbrechnungGesperrt, normalisiereBestandteile, paketDeckel, zaehlendeBestandteilKeys } from './bestandteile/bestandteilLogic';
import { KONTINGENT_MAX, stundenAusDb } from './bestandteile/stundenLogic';

/**
 * Lesezugriff auf das Leistungsverzeichnis eines Projekts.
 *
 * Extrahiert aus AdminAccountingView, wo diese Logik als nicht exportierte
 * Closure lag und damit weder testbar noch fuer das Rechnungsmodul erreichbar
 * war. Verhalten unveraendert uebernommen — die Funktionen sind die Grundlage
 * der bestehenden Buchhaltungsansicht UND der neuen Rechnungserstellung, und
 * beide muessen dieselben Zahlen sehen.
 *
 * Rein lesend und ohne Seiteneffekte: geschrieben wird ausschliesslich beim
 * Ausstellen einer Rechnung (invoiceService.issueInvoice).
 */

/** Projektzeile, wie sie aus Supabase kommt. Bewusst lose typisiert wie im Bestand. */
export interface ProjectRowLike {
    id?: string;
    name?: string;
    status?: string;
    project_number?: string;
    project_services?: unknown[];
    data?: { metadata?: ReportMetadata };
}

const byLvPosition = (a: ServiceItem, b: ServiceItem): number =>
    (a.lvPosition || '').localeCompare(b.lvPosition || '', undefined, { numeric: true });

/**
 * Die Definition als Vergleichstext: normalisiert, `null` fuer „keine"
 * (`[]` und reiner Bruch eingeschlossen). Genau EINE Form, damit Laden und
 * Speichern dieselbe Basis vergleichen (ServiceItem.bestandteileBasis).
 */
export const definitionsJson = (bestandteile: unknown): string =>
    // `[]` eigens: es ist ein Paket aus lauter unlesbaren Bestandteilen
    // (hatBestandteile), nicht „keine". Sein Entfernen (`null`) ist damit eine
    // Aenderung und geht mit.
    (Array.isArray(bestandteile) && bestandteile.length === 0
        ? '[]'
        : JSON.stringify(normalisiereBestandteile(bestandteile) ?? null));

/**
 * Kennung eines Ladevorgangs fuer `bestandteileBasis.herkunft` — eindeutig
 * auch ueber ein Neuladen der Seite hinweg (Zufallsteil je Laufzeit), damit
 * ein Warteschlangeneintrag aus einer frueheren Sitzung nie als „dieselbe
 * Herkunft" gilt wie ein frisch geladener Stand.
 */
const LADELAUF = Math.random().toString(36).slice(2, 10);
let ladeZaehler = 0;
const neueLadeHerkunft = (): string => `${LADELAUF}-${(++ladeZaehler).toString(36)}`;

/** Zahl der offenen Bestandteile aus der Zeile — alles Unlesbare ist `null` (= Gate unbekannt). */
const offeneBestandteileAusZeile = (roh: unknown): number | null => {
    const n = typeof roh === 'number'
        ? roh
        : typeof roh === 'string' && roh.trim() !== '' ? Number(roh) : Number.NaN;
    // Negativ oder keine Zahl ist kein Stand, sondern ein Fehler — und ein
    // Fehler darf nie „frei" heissen (fail closed).
    return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * STUNDENKONTINGENT (SQL Nr. 171/172, docs/stundenkontingent-vertrag.md): die
 * beiden Stunden-Felder einer `project_services`-Zeile — an GENAU dieser
 * Stelle abgebildet und von `bestandteilFelderAusZeile` mitgenommen, damit
 * jeder Ladeweg, der die Paketposition liest, auch ihre Stunden liest (eine
 * Lesart, kein zweiter Aufruf, den ein Ladeweg vergessen koennte).
 *
 * Je Feld, unabhaengig voneinander:
 *  - Spalte nicht gelesen (fehlt, Rueckfall, Spaltenliste ohne sie) → Feld
 *    bleibt WEG. `saveProjectToCloud` sendet dann keinen Schluessel.
 *  - Spalte gelesen → Feld gesetzt, aber NUR, wenn die Position ein Paket ist
 *    ODER der Wert nicht null ist. Gewoehnliche Positionen (Spalte NULL)
 *    bekommen so keinen neuen Schluessel — Listenvergleiche sehen dort nichts
 *    Neues. Ein Wert an einer Position ohne Bestandteile (Stunden zu
 *    inzwischen entfernten Bestandteilen, S4) bleibt dagegen sichtbar.
 *
 * `stundenErfasst` ist NUR LESEN: die Summe pflegt die Datenbank (Trigger aus
 * Nr. 172), gesendet wird sie nie.
 */
export const stundenFelderAusZeile = (
    row: { stunden_kontingent?: unknown; stunden_erfasst?: unknown } | null | undefined,
    istPaket: boolean,
): Pick<ServiceItem, 'stundenKontingent' | 'stundenErfasst'> => {
    const felder: Pick<ServiceItem, 'stundenKontingent' | 'stundenErfasst'> = {};
    if (row?.stunden_kontingent !== undefined) {
        const wert = stundenAusDb(row.stunden_kontingent);
        if (istPaket || wert !== null) felder.stundenKontingent = wert;
    }
    if (row?.stunden_erfasst !== undefined) {
        const wert = stundenAusDb(row.stunden_erfasst);
        if (istPaket || wert !== null) felder.stundenErfasst = wert;
    }
    return felder;
};

/**
 * Ein Kontingent, das der CHECK aus Nr. 171 annimmt: 0 < h ≤ 99999 (S9) —
 * ZUERST auf die Spaltengenauigkeit numeric(9,2) gerundet, dann geprueft.
 * Postgres prueft den CHECK am vorgeschlagenen INSERT-Satz noch VOR der
 * Konfliktaufloesung: ein 0,004 aus einer fremden Vorlage wuerde zu 0,00 und
 * liesse sonst bei JEDEM Speichern das ganze Projekt scheitern. Ungueltig →
 * undefined.
 */
const gueltigesKontingentWert = (wert: unknown): number | undefined => {
    if (typeof wert !== 'number' || !Number.isFinite(wert)) return undefined;
    const gerundet = Math.round(wert * 100) / 100;
    return gerundet > 0 && gerundet <= KONTINGENT_MAX ? gerundet : undefined;
};

/**
 * Das Kontingent fuer die Schreibzeile von `project_services` — Muster
 * `flat_rate`: der Schluessel steht nur drin, wenn die Position etwas dazu zu
 * sagen hat (`stundenKontingent !== undefined`). Ausdrueckliches `null` geht
 * als `null` (kein Kontingent). Gesendet wird es bei JEDEM Speichern, wirksam
 * ist es aber nur beim ANLEGEN der Position: bei einer bestehenden behaelt der
 * Trigger aus Nr. 172 den alten Wert (S10) — so setzt kein alter Tab ein
 * inzwischen vom Admin geaendertes Kontingent zurueck.
 *
 * Ein Wert ausserhalb des Bereichs (keine Zahl, ≤ 0, > 99999) bleibt WEG
 * statt gesendet zu werden: bei einer neuen Position liesse der CHECK aus
 * Nr. 171 sonst das GANZE Speichern scheitern, und ein „kein Schluessel"
 * heisst dort schlicht „kein Kontingent".
 *
 * `stunden_erfasst` steht hier nie — die Spalte gehoert der Datenbank.
 */
export const stundenKontingentZeile = (
    s: Pick<ServiceItem, 'stundenKontingent'>,
): { stunden_kontingent?: number | null } => {
    const wert: unknown = s.stundenKontingent;
    if (wert === undefined) return {};
    if (wert === null) return { stunden_kontingent: null };
    const gueltig = gueltigesKontingentWert(wert);
    return gueltig === undefined ? {} : { stunden_kontingent: gueltig };
};

/**
 * KATALOG (`service_catalog.stunden_kontingent`, Nr. 171): die Vorgabe des
 * Kontingents aus einer Katalogzeile (`select('*')`). Spalte fehlt oder NULL →
 * `undefined` (Muster `bestandteile` im Katalog: der Katalog arbeitet still
 * weiter). Ob das Kontingent beim Kopieren mitgeht, entscheidet der Aufrufer —
 * nur zusammen mit einer Bestandteil-Vorlage (S1).
 */
export const katalogKontingentAusZeile = (
    row: { stunden_kontingent?: unknown } | null | undefined,
): number | undefined => {
    return gueltigesKontingentWert(stundenAusDb(row?.stunden_kontingent));
};

/**
 * KATALOG: das Kontingent fuer das INSERT einer neuen Katalogposition. Nur
 * mit Bestandteil-Vorlage (S1) und nur ein gueltiger Wert — sonst KEIN
 * Schluessel, damit Positionen ohne Kontingent auch ohne die Spalte (Nr. 171
 * nicht gefahren) anlegbar bleiben (Muster `flat_rate`/`bestandteile` dort).
 */
export const katalogKontingentFuerZeile = (
    item: { bestandteile?: PositionsBestandteil[] | null; stundenKontingent?: number | null },
): { stunden_kontingent?: number } => {
    if (!hatBestandteile(item)) return {};
    const gueltig = gueltigesKontingentWert(item.stundenKontingent);
    return gueltig === undefined ? {} : { stunden_kontingent: gueltig };
};

/**
 * PAKETPOSITION (SQL Nr. 165/166): die beiden Bestandteil-Felder einer
 * `project_services`-Zeile — an GENAU dieser Stelle abgebildet. Jeder Ladeweg,
 * der Positionen fuer Geld, offene Mengen oder das Archiv liest (Projektliste,
 * loadProjectById, getNormalizedServices, Archiv-Riegel, Cockpit), geht hier
 * durch; zwei Lesarten derselben Spalte waeren zwei Gates.
 *
 * `bestandteile` (die Definition):
 *  - Spalte fehlt (Rueckfall, Migration nicht gefahren) → Feld bleibt WEG.
 *    `saveProjectToCloud` sendet dann keinen Schluessel und kann die
 *    Definition nicht still leeren (Muster `flat_rate`).
 *  - Spalte NULL → `null` („keine Bestandteile", ausdruecklich gelesen).
 *  - Array → PAKET, sobald es mindestens ein Objekt mit nicht leerem Key
 *    traegt, GLEICH WELCHEN TYPS — dieselbe Regel wie die Datenbank
 *    (`bestandteil_key`, Nr. 166; hier `zaehlendeBestandteilKeys`). Ob eine
 *    Position ein Paket ist, haengt nie an der Anzeige (Codex R1/P1 und
 *    R2a/P2: sonst Gate „kein" bei `bestandteile_offen = 1`).
 *  - Die LISTE fuehrt nur lesbare Bestandteile (`normalisiereBestandteile`:
 *    Key aus Text oder ganzer Zahl; leeres Label → Key, unbekannte Quelle →
 *    'hand'). Ein Key aus Objekt, Liste, Wahrheitswert oder Kommazahl fehlt in
 *    der Liste — die Datenbank zaehlt ihn, das Gate kommt aus
 *    `bestandteile_offen` und bleibt zu; der Dialog meldet „unlesbar". Sind
 *    ALLE Bestandteile unlesbar, steht `[]` mit dem Gate da
 *    (hatBestandteile wertet das als Paket).
 *  - Ein Array ohne zaehlendes Element (`[]`, nur Bruch) ist kein Paket →
 *    `null`, wie die Datenbank (`bestandteile_offen` NULL).
 *  Zurueckgeschrieben wird die gelesene Form nie unveraendert
 *  (cloudService.definitionSenden).
 *
 * `bestandteileOffen` (das Gate, von der DATENBANK gepflegt, NIE gesendet):
 *  nur bei einer Position MIT Bestandteilen — sonst bleibt das Feld weg, damit
 *  Listenvergleiche bei den gewoehnlichen Positionen keinen neuen Schluessel
 *  sehen. Fehlt der Wert oder ist er unlesbar, steht `null` da: Gate
 *  UNBEKANNT, die Position gilt als gesperrt (bestandteilLogic.bestandteilGate).
 *
 * `bestandteileBasis` (abgeleitet, nie geschrieben): der geladene Stand der
 *  Definition, gegen den saveProjectToCloud entscheidet, ob `bestandteile`
 *  mitgeht (ServiceItem.bestandteileBasis). Nur mit `id` — ohne sie gibt es
 *  nichts, woran die Basis haengen koennte, und solche Zeilen werden ohnehin
 *  nicht gespeichert (Archiv-Riegel und Stilllegung lesen nur Mengen).
 *
 * `stundenKontingent` / `stundenErfasst` (Nr. 171/172): kommen aus
 *  `stundenFelderAusZeile` mit — „Paket" heisst dort: die Definition oben ist
 *  eine Liste (auch `[]`). So liest jeder Ladeweg, der hier durchgeht, auch
 *  die Stunden; fehlen die Spalten, bleiben die Felder weg.
 */
export const bestandteilFelderAusZeile = (
    row: {
        id?: unknown; bestandteile?: unknown; bestandteile_offen?: unknown;
        stunden_kontingent?: unknown; stunden_erfasst?: unknown;
    } | null | undefined,
): Pick<ServiceItem, 'bestandteile' | 'bestandteileOffen' | 'bestandteileBasis' | 'stundenKontingent' | 'stundenErfasst'> => {
    const roh = row?.bestandteile;
    // Spalte nicht gelesen (fehlt, Rueckfall): zur Definition nichts zu sagen
    // — die Stunden nur, wo ein Wert steht (ohne Definition kein Paket).
    if (roh === undefined) return stundenFelderAusZeile(row, false);
    // Die LESBARE Liste (Anzeige, Buchen) und, getrennt davon, ob die
    // Datenbank ein Paket zaehlt — jedes Objekt mit nicht leerem Key, gleich
    // welcher Typ (Codex R2a). Die Paketeigenschaft haengt nie an der Liste.
    // Paket: die lesbare Liste, oder `[]`, wenn die Datenbank zaehlt, aber
    // kein Key lesbar ist (bestandteilLogic.hatBestandteile: `[]` IST ein
    // Paket). Kein Paket: `null`.
    const bestandteile: PositionsBestandteil[] | null = zaehlendeBestandteilKeys(roh) === 0
        ? null
        : (normalisiereBestandteile(roh) ?? []);
    const id = row?.id;
    // Die Basis traegt genau die gelesene Form (`[]` eigens, siehe
    // definitionsJson): gegen sie vergleicht der Speicherweg, und eine bloss
    // gelesene Definition geht so nie zurueck — auch nicht ohne ihre
    // unlesbaren Elemente.
    const basis = id === undefined || id === null || id === ''
        ? {}
        : { bestandteileBasis: { id: String(id), json: definitionsJson(bestandteile), herkunft: neueLadeHerkunft() } };
    if (bestandteile === null) return { bestandteile: null, ...basis, ...stundenFelderAusZeile(row, false) };
    // Das Gate aus der Datenbank: 0 frei, > 0 gesperrt, fehlt/kaputt null
    // (unbekannt = gesperrt) — auch bei `[]`.
    return {
        bestandteile,
        bestandteileOffen: offeneBestandteileAusZeile(row?.bestandteile_offen),
        ...basis,
        ...stundenFelderAusZeile(row, true),
    };
};

/**
 * Liefert die Leistungspositionen eines Projekts, nach LV-Nummer sortiert.
 *
 * Zwei Quellen, in dieser Reihenfolge:
 *  1. die relationale Tabelle project_services (massgeblich, sobald befuellt),
 *  2. sonst metadata.globalData.services aus dem Projekt-JSON.
 *
 * Der Vorrang ist nicht willkuerlich: saveProjectToCloud leert `services` im
 * JSON-Blob und persistiert die Positionen relational. Ein Projekt, das seit
 * dieser Umstellung gespeichert wurde, hat die Wahrheit nur noch in
 * project_services.
 */
export const getNormalizedServices = (project: ProjectRowLike | null | undefined): ServiceItem[] => {
    if (!project) return [];

    const relational = project.project_services;
    if (Array.isArray(relational) && relational.length > 0) {
        return stampFlatCoverage(relational
            .map((row): ServiceItem => {
                const s = row as Record<string, unknown>;
                return {
                    id: String(s.id),
                    lvPosition: (s.lv_position as string) ?? '',
                    clientLvPosition: s.client_lv_position as string | undefined,
                    title: (s.title as string) ?? '',
                    unit: (s.unit as string) ?? '',
                    unitPrice: Number(s.unit_price) || 0,
                    quantityContracted: Number(s.quantity_contracted) || 0,
                    quantityUsed: Number(s.quantity_used) || 0,
                    quantityBilled: Number(s.quantity_billed) || 0,
                    status: (s.status as ServiceItem['status']) ?? 'open',
                    category: s.category as string | undefined,
                    assignment: s.assignment as ServiceItem['assignment'],
                    // NEP (Eventualposition). Nur das ausdrueckliche true
                    // traegt das Feld — sonst bleibt die Position, wie sie
                    // war; Aufrufer, die Listen vergleichen, sehen keinen
                    // neuen Schluessel.
                    ...(s.is_optional === true ? { is_optional: true } : {}),
                    // Fehlt die Spalte (Migration noch nicht gefahren) oder ist
                    // sie leer, bleibt es beim Altverhalten: Vorbelegung 1.
                    minHours: s.min_hours == null ? undefined : Number(s.min_hours),
                    // Verzicht. Fehlt die Spalte, ist nichts verzichtet — die
                    // Position verhaelt sich wie bisher.
                    quantityWaived: s.quantity_waived == null ? undefined : Number(s.quantity_waived),
                    waivedReason: (s.waived_reason as string | null) ?? undefined,
                    waivedAt: (s.waived_at as string | null) ?? undefined,
                    waivedBy: (s.waived_by as string | null) ?? undefined,
                    // WELCHE Leistungen der Verzicht meint (SQL Nr. 70) —
                    // dieselbe Aussage wie im Ladeweg (cloudService:
                    // `waived_entry_ids ?? undefined`). Fehlte sie hier, sah
                    // jede Ansicht auf der Listenzeile eine verzichtete
                    // Begehung wieder als waehlbar (Verzichtsdialog der
                    // Buchhaltung), und der kumulative Stand zum Stichtag zog
                    // einen Verzicht NACH dem Stichtag ab. Nur eine Liste
                    // traegt das Feld (die Datenbank laesst per CHECK nichts
                    // anderes zu); fehlt die Spalte oder steht NULL, bleibt es
                    // weg — Verzicht "nur Menge", wie bisher, und kein neuer
                    // Schluessel an den Positionen ohne Verzicht.
                    ...(Array.isArray(s.waived_entry_ids) ? { waivedEntryIds: s.waived_entry_ids as string[] } : {}),
                    // Monatspauschale. Fehlt die Spalte (Migration noch nicht
                    // gefahren), gibt es keine Pauschale — alles wie bisher.
                    flatRate: (s.flat_rate as FlatRateCoverage | null) ?? undefined,
                    // Herkunft nach einem Umzug von einer Tochter (SQL Nr. 68).
                    // Fehlt die Spalte, lag die Position immer schon hier —
                    // genau die Aussage, die NULL hier traegt.
                    transferredFrom: (s.transferred_from as ServiceItem['transferredFrom']) ?? undefined,
                    // Paketposition (SQL Nr. 165/166): Definition und Gate,
                    // dazu die Stunden (Nr. 171). Fehlen die Spalten, bleiben
                    // die Felder weg — die Lesart steht an
                    // bestandteilFelderAusZeile.
                    ...bestandteilFelderAusZeile(s),
                };
            })
            .sort(byLvPosition));
    }

    const services = project.data?.metadata?.globalData?.services || [];
    return stampFlatCoverage([...services].sort(byLvPosition));
};

/**
 * MONATSPAUSCHALE: stempelt die aufgeloeste Deckung als abgeleitetes Feld
 * `flatCoveredBy` auf die Positionen (siehe ServiceItem.flatCoveredBy).
 *
 * Der Stempel entsteht an genau dieser einen Stelle, gegen die VOLLSTAENDIGE
 * Positionsliste — danach koennen openQuantity (sieht nur die einzelne
 * Position) und die Rechnungslogik (arbeitet auch mit gefilterten Listen) die
 * Deckung lesen, ohne die Pauschalposition noch in der Hand zu haben. Die
 * Aufloesung selbst liegt in flatRateLogic.resolveFlatCoverage; hier gibt es
 * keine eigene Regel.
 *
 * Ohne Pauschale und ohne Altstempel kommt dieselbe Liste unveraendert
 * zurueck — Aufrufer, die sich auf Objektidentitaet verlassen, sehen fuer
 * die 99 % Projekte ohne Pauschale keinen Unterschied. Ein veralteter
 * Stempel (Pauschale entfernt oder umgestellt) wird abgeraeumt: er ist eine
 * Ableitung und darf den Zustand, aus dem er stammt, nicht ueberleben.
 */
export const stampFlatCoverage = (services: ServiceItem[]): ServiceItem[] => {
    const coverage = resolveFlatCoverage(services);
    if (coverage.size === 0 && !services.some((s) => s.flatCoveredBy !== undefined)) {
        return services;
    }
    return services.map((s) => {
        const coveredBy = coverage.get(s.id);
        if (coveredBy === s.flatCoveredBy) return s;
        return { ...s, flatCoveredBy: coveredBy };
    });
};

export interface ProjectFinancials {
    /** Auftragswert: beauftragte Menge mal Einzelpreis. */
    total: number;
    /** Bereits abgerechnet. */
    billed: number;
    /** Erbracht (Leistungsstand). */
    used: number;
    /** Erbracht, aber noch nicht abgerechnet — die abrechenbare Groesse. */
    open: number;
    /**
     * Derselbe Betrag OHNE die Mengen, die eine Alt-Rechnung bereits abgegolten
     * hat (ALT-Buchungen im Leistungsprotokoll) — die Groesse fuer die ANZEIGE.
     *
     * Nachgetragene Alt-Leistungen tragen quantityBilled 0 und muessen das auch:
     * abgerechnet wird der Gesamtstand von der kumulativen Rechnung, deren Abzug
     * die Alt-Abschlaege enthaelt. In `open` stehen sie deshalb als offen — und
     * genau das ist die Meldung "nicht abgerechnet", die aus der Buchhaltung
     * kommt.
     *
     * BEWUSST ein ZWEITER Wert und keine Aenderung an `open`: an `open` haengen
     * die Sichtbarkeitsregeln der Buchhaltungsansicht (Filter "Nur
     * Abrechenbare", "Ab Summe", das Ausblenden abgeschlossener Projekte ohne
     * offenen Posten). Wuerde `open` schrumpfen, verschwaenden Projekte aus der
     * Liste — eine Anzeigekorrektur darf nichts unsichtbar machen.
     */
    displayOpen: number;
    /**
     * PAKETPOSITION: Wert der erbrachten, aber ZURUECKGEHALTENEN Mengen —
     * Paketpositionen, deren Bestandteile noch nicht alle erledigt sind oder
     * deren Stand nicht ladbar ist (bestandteilLogic.istAbrechnungGesperrt).
     *
     * Er steckt NICHT in `open`: zurueckgehaltenes Geld darf heute niemand
     * fordern, und an `open` haengen die Filter „Nur Abrechenbare" und „Ab
     * Summe". Aber er ist offenes Geld von morgen — und genau deshalb ein
     * eigener Wert und keine stille Null: jeder Leser, der aus `open <= 0`
     * „fertig, archivierbar" schliesst, muss ihn als Sperrgrund sehen
     * (hasOpenBalance). Sonst archivierte sich ein Projekt mit unabgerechnetem
     * Paket selbst (Plan, Kernregel 4 „fail closed").
     */
    heldByComponents: number;
}

/**
 * Traegt das Projekt noch Geld — abrechenbar ODER zurueckgehalten?
 *
 * Die Frage jedes Lesers, der aus den Kennzahlen „erledigt" ableitet
 * (Archivliste, Historie, Statusplakette). `open` allein reicht dafuer seit
 * den Paketpositionen nicht mehr: eine gebuchte Paketposition mit offenen
 * Bestandteilen hat `open` 0 und ist trotzdem nicht erledigt.
 */
export const hasOpenBalance = (f: Pick<ProjectFinancials, 'open' | 'heldByComponents'>): boolean =>
    f.open > 0 || (f.heldByComponents ?? 0) > 0;

/**
 * Wertmaessige Kennzahlen eines Projekts, in Euro als Float.
 *
 * Bewusst weiter in Euro-Floats: das ist die Bestandsdarstellung des LV und
 * wird von der Buchhaltungsansicht so erwartet. Die Umrechnung nach Cent
 * passiert erst beim Erzeugen einer Rechnungsposition, an genau einer Stelle
 * (money.centsFromEuro) — danach wird nie wieder aus dem Float gerechnet.
 *
 * Fallback fuer Altprojekte ohne LV: globalData.scope mit Pauschalfeldern.
 * Dieser Zweig wird nirgends mehr geschrieben, existiert aber in Bestandsdaten.
 */
export const getProjectFinancials = (
    project: ProjectRowLike | null | undefined,
): ProjectFinancials => {
    const services = getNormalizedServices(project);
    let total = 0;
    let billed = 0;
    let used = 0;
    // Wert der Mengen, die eine Alt-Rechnung schon gefordert hat.
    //
    // ZWEI Deckel, und beide sind noetig. quantityBilled zehrt die AELTESTEN
    // Mengen auf, und das sind die Alt-Mengen: was regulaer abgerechnet wurde,
    // ist keine offene Alt-Deckung mehr (max(0, alt - billed)). Ohne diesen
    // ersten Deckel gilt nach der ersten kumulativen Rechnung (billed = used)
    // jede NEUE Einheit dieser Position dauerhaft als gedeckt — sie
    // verschwaende still aus der Abrechnung. Der zweite Deckel begrenzt auf das
    // an der Position ueberhaupt Offene.
    let altCoveredValue = 0;
    // Wert der Mengen, auf die ausdruecklich verzichtet wurde. Sie sind
    // erbracht (und bleiben deshalb in `used`), werden aber nicht mehr
    // gefordert — siehe ServiceItem.quantityWaived.
    let waivedValue = 0;
    // MONATSPAUSCHALE: Wert der offenen Mengen GEDECKTER Positionen. Wie der
    // Verzicht bleiben sie in `used` (erbracht ist erbracht, der Nachweis
    // steht), sind aber kein offenes Geld — die Pauschale gilt sie ab.
    let flatCoveredValue = 0;
    // PAKETPOSITION: Wert der offenen Mengen, deren Bestandteile noch nicht
    // vollstaendig sind (oder deren Stand fehlt) — zurueckgehalten, nicht
    // abrechenbar. Eigener Rueckgabewert `heldByComponents`.
    let heldValue = 0;
    // PAKETPOSITION: Wert der Mengen UEBER dem Deckel (Vertragsmenge minus
    // abgerechnet, Kernregel 3) — eine doppelte Positionsbuchung. Sie wird nie
    // gefordert (openQuantity deckelt), also ist sie auch kein offenes Geld.
    let ueberDeckelValue = 0;
    const altCovered = altCoveredQuantities(
        getServiceLog(project?.data),
    );

    services.forEach((s) => {
        // NEP zaehlt erst, was gebucht ist (contractedQuantity): der
        // Auftragswert waechst mit der Nachbeauftragung, nicht mit dem Angebot.
        total += contractedQuantity(s) * s.unitPrice;
        used += s.quantityUsed * s.unitPrice;
        billed += (s.quantityBilled || 0) * s.unitPrice;
        const waivedQty = waivedQuantity(s);
        waivedValue += waivedQty * s.unitPrice;
        const billedQty = s.quantityBilled || 0;
        // Das an der Position noch OFFENE, nach Abzug des Verzichts — die
        // Groesse, die entweder die Pauschale oder eine Alt-Rechnung deckt.
        let openQty = Math.max(0, (s.quantityUsed || 0) - billedQty - waivedQty);
        if (s.flatCoveredBy) {
            flatCoveredValue += openQty * s.unitPrice;
            // Keine Alt-Deckung mehr dazurechnen: offen ist hier nichts, und
            // ein zweiter Abzug fuer dieselbe Menge drueckte `displayOpen`
            // unter die Wahrheit.
            return;
        }
        // PAKETPOSITION — dieselben zwei Regeln wie in openQuantity, in
        // derselben Reihenfolge: erst der Deckel, dann das Gate. Gesperrt
        // heisst „zurueckgehalten", nicht „bezahlt": der Wert wandert in
        // `heldByComponents` und nicht ins Nichts.
        if (hatBestandteile(s)) {
            const gedeckelt = Math.min(openQty, paketDeckel(s));
            ueberDeckelValue += (openQty - gedeckelt) * s.unitPrice;
            if (istAbrechnungGesperrt(s)) {
                heldValue += gedeckelt * s.unitPrice;
                // Keine Alt-Deckung: abrechenbar ist hier nichts, ein zweiter
                // Abzug drueckte `displayOpen` unter die Wahrheit (wie oben).
                return;
            }
            openQty = gedeckelt;
        }
        const alt = altCovered.get(s.id) ?? 0;
        if (alt > 0) {
            const effectiveAlt = Math.max(0, alt - billedQty);
            // Der Deckel ist das an der Position noch OFFENE, also nach Abzug
            // des Verzichts. Ohne ihn zoege eine verzichtete Menge zusaetzlich
            // eine Alt-Deckung ab und `displayOpen` faende einen zweiten Grund
            // fuer dieselbe Null.
            altCoveredValue += Math.min(effectiveAlt, openQty) * s.unitPrice;
        }
    });

    if (services.length === 0) {
        const meta = project?.data?.metadata;
        const scope = meta?.globalData?.scope;
        if (scope && (scope.totalInspections ?? 0) > 0 && scope.pricePerInspection) {
            total += (scope.totalInspections ?? 0) * scope.pricePerInspection;
            used += (meta?.inspectionNumber || 0) * scope.pricePerInspection;
            billed += (scope.billedInspections || 0) * scope.pricePerInspection;
        }
    }

    // Der Verzicht geht hier UND nicht nur in die Anzeige: an `open` haengt,
    // ob ein schlussgerechnetes Projekt aus der Buchhaltungsliste in das
    // Archiv wandert (AdminAccountingView: `isFinished && open <= 0`). Stuende
    // er nur in `displayOpen`, bliebe genau der Fall stehen, fuer den es ihn
    // gibt.
    //
    // Die Pauschalen-Deckung aus demselben Grund: eine gedeckte Position ist
    // kein offenes Geld, sonst hielte jedes Pauschalprojekt dauerhaft einen
    // Posten offen, den nie jemand abrechnen darf.
    //
    // PAKETPOSITION: zurueckgehaltene Mengen und der Ueberhang ueber dem
    // Deckel gehen ebenfalls aus `open` heraus — die Buchhaltungsliste soll
    // dieselbe Zahl zeigen, die eine Rechnung fordern darf. Der
    // zurueckgehaltene Teil steht dafuer in `heldByComponents`; wer aus
    // `open <= 0` auf „erledigt" schliesst, fragt hasOpenBalance.
    const heldByComponents = Math.max(0, heldValue);
    const open = Math.max(0, used - billed - waivedValue - flatCoveredValue - heldByComponents - ueberDeckelValue);
    return { total, billed, used, open, displayOpen: Math.max(0, open - altCoveredValue), heldByComponents };
};

/**
 * Menge, auf die ausdruecklich verzichtet wurde — erbracht, wird aber nicht
 * mehr gefordert (ServiceItem.quantityWaived).
 *
 * Gedeckelt auf das, was ueberhaupt erbracht ist: ein Verzicht ueber mehr als
 * `quantityUsed` ist keine Aussage, sondern ein Zahlendreher, und er duerfte
 * niemals eine spaeter hinzukommende Leistung mit verschlucken.
 */
export const waivedQuantity = (service: ServiceItem): number =>
    Math.max(0, Math.min(service.quantityWaived || 0, service.quantityUsed || 0));

/**
 * Noch abrechenbare Menge einer Position: erbracht minus abgerechnet minus
 * verzichtet minus bereits von einem offenen Rechnungsentwurf reserviert.
 *
 * Die Reservierung ist die erste von drei Idempotenz-Schichten des
 * Rechnungslaufs. Ohne sie wuerde ein zweiter Lauf ueber dieselbe Auswahl die
 * gleichen Positionen erneut in einen Entwurf ziehen.
 *
 * Der Verzicht ist die EINE Stelle, an der die Buchhaltung eine erbrachte,
 * aber nie abgerechnete Leistung schliessen kann (siehe ServiceItem.
 * quantityWaived). Er steht hier und nicht in den Aufrufern, weil an dieser
 * Funktion die Rechnungsauswahl, der Projektcheck und der Rechnungslauf
 * gemeinsam haengen — eine zweite Wahrheit dafuer waere genau die Sorte
 * Doppelregel, an der dieses Modul schon einmal auseinandergelaufen ist.
 *
 * MONATSPAUSCHALE: eine gedeckte Position (flatCoveredBy, gestempelt in
 * stampFlatCoverage) hat NIE offene Menge — ihr Geld ist mit der Pauschale
 * abgegolten, egal wie weit `quantityUsed` noch laeuft. Kernregel: der
 * Leistungsstand laeuft weiter (Nachweis, Begehungszaehler, Rhythmus), der
 * GELDSTAND steht still. Aus demselben Grund wie beim Verzicht steht die
 * Regel HIER und nicht in den Aufrufern: Rechnungsauswahl, Projektcheck und
 * Rechnungslauf haengen gemeinsam an dieser Funktion.
 *
 * PAKETPOSITION (docs/positionsbestandteile-plan.md, Kernregeln 2–4) — zwei
 * Regeln, aus demselben Grund an derselben Stelle:
 *  - GATE: solange nicht jeder Bestandteil erledigt oder entfallen ist, hat
 *    die Position offene Menge 0. Fail closed: ist der Stand unbekannt
 *    (`bestandteileOffen` fehlt), gilt sie ebenfalls als gesperrt. Wie bei der
 *    Deckung laeuft `quantityUsed` weiter — gebucht ist gebucht, nur der
 *    GELDSTAND wartet. Was hier zurueckgehalten wird, zeigt
 *    heldByComponentsQuantity (und getProjectFinancials.heldByComponents).
 *  - DECKEL: nie mehr als Vertragsmenge minus abgerechnet. Zwei Sitzungen,
 *    die gleichzeitig den letzten Bestandteil erledigen, buchen die Position
 *    zweimal — gefordert wird sie trotzdem nur einmal. Die Reservierung eines
 *    anderen Entwurfs zieht NACH dem Deckel ab, sonst boete ein zweiter
 *    Entwurf die zweite (falsche) Buchung an.
 * Ohne Bestandteile bleibt die Rechnung zeichengleich die alte.
 */
export const openQuantity = (service: ServiceItem, reservedQty = 0): number => {
    if (service.flatCoveredBy) return 0;
    if (istAbrechnungGesperrt(service)) return 0;
    const roh = (service.quantityUsed || 0)
        - (service.quantityBilled || 0)
        - waivedQuantity(service);
    if (!hatBestandteile(service)) return Math.max(0, roh - reservedQty);
    return Math.max(0, Math.min(roh, paketDeckel(service)) - reservedQty);
};

/**
 * PAKETPOSITION: die erbrachte, aber ZURUECKGEHALTENE Menge — was openQuantity
 * ohne das Gate liefern wuerde (gedeckelt, nach Verzicht, ohne Reservierung).
 * 0 fuer jede Position ohne Bestandteile, mit freiem Gate oder mit
 * Pauschaldeckung.
 *
 * Fuer jeden, der aus „nichts offen" auf „fertig" schliesst (Auto-Archiv,
 * Archiv-Riegel): eine gebuchte Paketposition mit offenen Bestandteilen ist
 * nicht abrechenbar, aber auch nicht erledigt.
 */
export const heldByComponentsQuantity = (service: ServiceItem): number => {
    if (service.flatCoveredBy || !istAbrechnungGesperrt(service)) return 0;
    const roh = (service.quantityUsed || 0)
        - (service.quantityBilled || 0)
        - waivedQuantity(service);
    return Math.max(0, Math.min(roh, paketDeckel(service)));
};

/**
 * Die Zelle „Offen (Abrechenbar)" der Buchhaltung (AdminAccountingView, je
 * Position): openQuantity ohne Reservierung, zusaetzlich um den noch nicht
 * regulaer abgerechneten Teil einer Alt-Deckung bereinigt.
 *
 * Stand frueher als eigene Formel in der Komponente (used − billed − Verzicht
 * − Alt-Deckung, Pauschale 0). Eine eigene Formel daneben kennt jede neue
 * Regel erst, wenn jemand daran denkt — bei den Paketpositionen haette sie
 * eine gesperrte Position als abrechenbar gezeigt. Jetzt liest sie Verzicht,
 * Pauschale, Gate und Deckel aus openQuantity; fuer Positionen ohne Paket ist
 * das Ergebnis zeichengleich das alte.
 *
 * `altQty`: die aus `ALT-`-Buchungen gedeckte Menge der Position. quantityBilled
 * zehrt die AELTESTEN Mengen auf, also die Alt-Mengen — nur der Rest zaehlt.
 */
export const accountingOpenQuantity = (service: ServiceItem, altQty = 0): number => {
    const effectiveAlt = Math.max(0, (altQty || 0) - (service.quantityBilled || 0));
    return Math.max(0, openQuantity(service) - effectiveAlt);
};

/**
 * „4/5" fuer eine Paketposition — allein aus der Definition (Anzahl) und dem
 * von der Datenbank gepflegten `bestandteileOffen`. null, wenn das nicht
 * belastbar geht: keine Bestandteile, Stand unbekannt, oder die Datenbank
 * zaehlt mehr offene, als die Liste Eintraege hat (dann waere jede Zahl
 * erfunden).
 *
 * Bewusst OHNE Eintraege und ohne Bestandteilnamen: die Geldpfade laden keine
 * Eintraege, und die Rechnungsleiste soll keine internen Namen tragen.
 */
export const bestandteilStandKurz = (
    service: Pick<ServiceItem, 'bestandteile' | 'bestandteileOffen'>,
): string | null => {
    const gesamt = normalisiereBestandteile(service?.bestandteile)?.length ?? 0;
    const gate = bestandteilGate(service);
    if (gesamt === 0 || gate === 'kein' || gate === 'unbekannt') return null;
    const offen = Number(service.bestandteileOffen);
    if (!Number.isFinite(offen) || offen < 0 || offen > gesamt) return null;
    const erledigt = Math.round((gesamt - offen) * 1000) / 1000;
    return `${erledigt}/${gesamt}`;
};

/**
 * Klartext, WARUM eine Paketposition nicht abrechenbar ist — fuer Rechnungs-
 * leiste, Buchhaltung und Archiv-Riegel. Ohne Bestandteilnamen.
 *  - Stand bekannt:   „Bestandteile 4/5"
 *  - Stand unbekannt: „Bestandteile noch nicht vollständig (Stand nicht ladbar)"
 *    — fail closed heisst gesperrt MIT sichtbarem Grund (Kernregel 4).
 *  - sonst:           „Bestandteile noch nicht vollständig"
 */
export const bestandteilSperrText = (service: Pick<ServiceItem, 'bestandteile' | 'bestandteileOffen'>): string => {
    const kurz = bestandteilStandKurz(service);
    if (kurz) return `Bestandteile ${kurz}`;
    return bestandteilGate(service) === 'unbekannt'
        ? 'Bestandteile noch nicht vollständig (Stand nicht ladbar)'
        : 'Bestandteile noch nicht vollständig';
};

/** True, wenn an der Position noch etwas abzurechnen ist. */
export const hasOpenQuantity = (service: ServiceItem, reservedQty = 0): boolean =>
    openQuantity(service, reservedQty) > 0;

/**
 * Stundenerkennung — liegt in serviceUnits.ts, weil externalAdvances sie
 * ebenfalls braucht und ein Import von hier einen Zyklus schloesse (dieses
 * Modul liest seinerseits altCoveredQuantities aus externalAdvances). Hier nur
 * durchgereicht, damit die bestehenden Aufrufer unveraendert bleiben.
 */
export {
    isHourlyUnit,
    defaultHoursForService,
    agreedMinHours,
    initialBookingAmountText,
    bookableQuantities,
    readMinHoursInput,
    applyMinHoursInput,
};

/** Das Wenige einer Position, aus dem sich ergibt, ob sie Begehungen zaehlt. */
export interface InspectionCountLike {
    assignment?: string;
    lvPosition?: string;
    title?: string;
    unit?: string;
    /** Deckungsobjekt einer Monatspauschale — eine solche Position zaehlt nie Begehungen. */
    flatRate?: FlatRateCoverage;
}

/** Das Deckungsobjekt einer Position — `flatRate` (ServiceItem) oder `flat_rate` (Zeile aus project_services). */
const flatRateField = (s: { flatRate?: unknown; flat_rate?: unknown } | null | undefined): FlatRateCoverage | undefined => {
    const raw = s?.flatRate ?? s?.flat_rate;
    return raw && typeof raw === 'object' ? (raw as FlatRateCoverage) : undefined;
};

/**
 * Ist diese Position eine Monatspauschale — als ServiceItem oder als Zeile aus
 * project_services?
 *
 * Eine Monatspauschale ist nie eine Begehungsposition, auch wenn ihr Titel
 * „Begehung" traegt oder ihre LV-Nummer unter 2.7./2.8. steht: ihr quantityUsed
 * zaehlt gebuchte MONATE (FLAT-JJJJ-MM). In einer Begehungsmenge blaehte jeder
 * Monat den Zaehler um eins auf — und applyInspectionCounter schriebe den
 * Begehungszaehler als Monatsmenge auf die Pauschale, also als Geld.
 * Exportiert fuer Aufrufer, die nur die Tabellenzeile kennen (Kacheln, Cockpit).
 */
export const isFlatRatePosition = (s: { flatRate?: unknown; flat_rate?: unknown } | null | undefined): boolean =>
    isFlatRateService({ id: '', flatRate: flatRateField(s) });

/**
 * Traegt diese Position den BEGEHUNGSZAEHLER in quantityUsed?
 *
 * Nur solche Positionen darf der Nachtragsdialog beim Oeffnen mit dem
 * Live-Zaehler ueberschreiben. Eine Stundenposition traegt dort einen
 * Stundenstand; ihn mit dem Zaehler zu ersetzen machte aus 28 Stunden 22 und
 * schrieb die Differenz beim Speichern als Korrektur ins Protokoll.
 */
export const isInspectionCountService = (s: InspectionCountLike): boolean =>
    !isFlatRatePosition(s) &&
    !isHourlyUnit(s.unit) &&
    (s.assignment === 'inspection' ||
        s.lvPosition === '2.7.10' ||
        (s.title || '').toLowerCase().includes('begehung'));

/**
 * Setzt den Begehungszaehler des Projekts auf die Position, die ihn fuehrt.
 *
 * Der Nachtragsdialog zeigt den IST-Stand der Begehungen aus dem Zaehler des
 * Projekts, weil der Zaehler dort die Wahrheit ist — er wird beim Versand
 * hochgezaehlt, das Leistungsverzeichnis nicht.
 *
 * ZWEI Schranken, und beide stammen aus Schaeden:
 *
 * 1. NUR bei genau EINER zaehlenden Position. Fuehrt ein Projekt mehrere
 *    Begehungsarten — "turnusmaessig 14-taeglich" und "woechentlich", oder
 *    turnusmaessig und anlassbezogen —, dann traegt der Zaehler die Summe und
 *    gehoert keiner einzelnen. Auf jede geschrieben erschiene dieselbe Arbeit
 *    mehrfach: Projekt 26071010 hatte vier Begehungen protokolliert und stand
 *    danach mit 4 auf der einen UND 4 auf der anderen Position — 720,00 EUR,
 *    zu denen es keine Leistung gibt.
 *
 * 2. NUR bei einem Zaehler groesser null. Ein Bestandsprojekt ohne gepflegten
 *    Zaehler haette sonst seinen erbrachten Stand auf 0 gesetzt bekommen.
 *
 * `proofCount` (ownReportProofCount): im Pauschalprojekt stehen die
 * Begehungen seit der Umstellung als Nachweiszeilen auf der Pauschale, die
 * Begehungsposition waechst nicht mehr. Der Zaehler zaehlt beide — auf die
 * Position gehoert nur sein Anteil ohne die Nachweise EIGENER Berichte
 * (Hand- und Tochternachweise vergeben keine Nummer). Mit dem vollen Zaehler
 * zeigte der Dialog 61 statt 55 und schriebe die 6 beim Speichern als
 * Korrektur auf die Position: dieselben Begehungen ein zweites Mal als Geld.
 * Schranke 2 gilt fuer diesen Anteil.
 *
 * Aendert sonst nichts: die uebrigen Positionen kommen unveraendert zurueck,
 * und ohne Treffer wird dieselbe Liste zurueckgegeben.
 *
 * Seit 18.10.2026 ruft der Nachtragsdialog sie NICHT mehr auf: die
 * Uebernahme schrieb bei jedem Speichern die Differenz zum Zaehler als
 * Korrektur ins Protokoll (5805, 6070-3) — siehe
 * projectSupplementLogic.prepareSupplementServices. Nicht wieder anschliessen.
 */
export const applyInspectionCounter =<T extends InspectionCountLike & { quantityUsed?: number }>(
    services: T[],
    liveCount: number,
    proofCount = 0,
): T[] => {
    const lvCount = liveCount - (proofCount > 0 ? proofCount : 0);
    if (!(lvCount > 0)) return services;
    const carriers = services.filter((s) => isInspectionCountService(s));
    if (carriers.length !== 1) return services;
    const carrier = carriers[0];
    return services.map((s) => (s === carrier ? { ...s, quantityUsed: lvCount } : s));
};

/**
 * Zaehlt diese Position in die Summe, die als neuer Begehungszaehler
 * zurueckgeschrieben wird?
 *
 * Weiter gefasst als isInspectionCountService (ganze Gruppen 2.7./2.8.) —
 * genau so stand es im Dialog. Die Stundenausnahme ist auch hier tragend: sonst
 * zaehlte der Begehungszaehler Stunden mit. Eine Pauschale unter 2.7./2.8.
 * ebenso wenig — sonst zaehlte er Monate mit.
 */
export const countsTowardInspectionCounter = (s: InspectionCountLike): boolean =>
    !isFlatRatePosition(s) &&
    !isHourlyUnit(s.unit) &&
    (s.assignment === 'inspection' ||
        !!(s.lvPosition && (s.lvPosition.startsWith('2.7.') || s.lvPosition.startsWith('2.8.'))));

/** Das Wenige einer Protokollzeile, aus dem sich ein Begehungsnachweis ergibt. */
export interface InspectionProofLogLike {
    serviceId?: string | null;
    bookingReference?: string | null;
    reportRef?: string | null;
    description?: string | null;
    /** Nur fuer ownReportProofCount: steht derselbe Bericht auch als MENGE auf einer anderen Position? Roh wie im Protokoll, gelesen per Number(). */
    quantity?: unknown;
}

/**
 * Das Wenige einer Position fuer inspectionProofCount — als ServiceItem
 * (`flatRate`) ODER als Zeile aus project_services (`flat_rate`). Beide Formen,
 * weil Kacheln und Cockpit die rohe Tabellenzeile lesen; jede eigene
 * Uebersetzung dort waere eine zweite Stelle, an der die Deckung gelesen wird.
 */
export interface InspectionProofServiceLike {
    id?: string | null;
    flatRate?: unknown;
    flat_rate?: unknown;
    /** Nur fuer ownReportProofCount: ist die Position der Zaehlertraeger (isInspectionCountService)? */
    assignment?: string;
    lvPosition?: string;
    title?: string;
    unit?: string;
}

const proofFlatOf = (s: InspectionProofServiceLike | null | undefined): FlatCoverageServiceLike | null => {
    if (!s?.id) return null;
    const flat: FlatCoverageServiceLike = { id: s.id, flatRate: flatRateField(s) };
    return isFlatRateService(flat) ? flat : null;
};

type ProofServices = ReadonlyArray<InspectionProofServiceLike | null | undefined> | null | undefined;
type ProofLog = ReadonlyArray<InspectionProofLogLike | null | undefined> | null | undefined;

/** Die Pauschalen der Liste nach id — leer im Normalfall (kein Pauschalprojekt). */
const proofFlatsById = (services: ProofServices): Map<string, FlatCoverageServiceLike> => {
    const flats = new Map<string, FlatCoverageServiceLike>();
    for (const s of services || []) {
        const flat = proofFlatOf(s);
        if (flat) flats.set(flat.id, flat);
    }
    return flats;
};

/** Zaehlt die Zeilen, die `isProof` gegen ihre Pauschale annimmt — je Referenz einmal. */
const countProofRefs = (
    flats: Map<string, FlatCoverageServiceLike>,
    serviceLog: ProofLog,
    isProof: (entry: InspectionProofLogLike, flat: FlatCoverageServiceLike) => boolean,
): number => {
    const refs = new Set<string>();
    for (const entry of serviceLog || []) {
        if (!entry?.serviceId) continue;
        const flat = flats.get(entry.serviceId);
        if (flat && isProof(entry, flat)) refs.add(String(entry.bookingReference).trim());
    }
    return refs.size;
};

/**
 * Wie viele Begehungen stehen als NACHWEISZEILE auf einer Pauschale — fuer die
 * ANZEIGE (Kacheln, Cockpit, Rhythmus, Liegezeit)?
 *
 * Im Pauschalprojekt bucht der Versand eines Berichts seit 30.09.2026 eine
 * Nachweiszeile (Menge 0, 'NW-…') auf die Pauschale; die Begehungsposition
 * (2.7.10) bekommt nichts mehr. Jede Stelle, die Begehungen aus quantityUsed
 * der Begehungspositionen zaehlt, bliebe ab der Umstellung stehen. Sie rechnet
 * diese Zahl hinzu — EINE Definition fuer alle Anzeigen.
 *
 * Gezaehlt wird, was isInspectionProofEntry gegen die Pauschale DER ZEILE als
 * Begehung wertet — also auch Hand-Begehungen und Tochterberichte; jede
 * Referenz nur einmal — ein Bericht ist eine Begehung, auch wenn seine
 * Nachweiszeile doppelt im Protokoll stuende.
 *
 * NICHT fuer den Abgleich mit dem Berichtszaehler: dafuer ownReportProofCount.
 */
export const inspectionProofCount = (services: ProofServices, serviceLog: ProofLog): number => {
    // Ohne Pauschale gar nicht erst durchs Protokoll: die Kacheln rufen das je
    // Projekt und je Render, und fast kein Projekt hat eine Pauschale.
    const flats = proofFlatsById(services);
    if (flats.size === 0) return 0;
    return countProofRefs(flats, serviceLog, isInspectionProofEntry);
};

/**
 * Wie viele Nachweiszeilen auf einer Pauschale belegen einen EIGENEN Bericht —
 * verbrauchen also eine Nummer des Berichtszaehlers (current_inspection_number)?
 *
 * Fuer jede Stelle, die den Zaehler gegen das LV haelt (Nachtragsdialog,
 * Projektcheck): Zaehler = Stand der Begehungsposition + dieser Anteil. Eine
 * Hand-Begehung ('NW-BEG-…' oder ohne Bericht auf einer reinen
 * Begehungspauschale) vergibt keine Nummer, ein Tochterbericht ('RPT-CHILD-…')
 * zaehlt in SEINEM Projekt. Mit ihnen (inspectionProofCount) zog der Dialog
 * 2.7.10 unter die echten Buchungen — Zaehler 57, zwei Berichtsnachweise, ein
 * Hand- und ein Tochternachweis ergaben 53 statt 55, jedes Speichern schrieb
 * −2 als Korrektur, und billed 55 > used 53 sperrte die Rechnung.
 */
export const ownReportProofCount = (services: ProofServices, serviceLog: ProofLog): number => {
    const flats = proofFlatsById(services);
    if (flats.size === 0) return 0;
    // Berichte, die ZUSAETZLICH als Menge auf einer anderen Position stehen:
    // aufgeteilt auf die Pauschale und ein NICHT gedecktes Los (Codex R2, P1).
    // Dort zaehlt der Bericht schon im Stand des Loses; den Nachweis noch
    // einmal abzuziehen setzte das Los im Nachtragsdialog unter seine echten
    // Buchungen — zehn Berichte + zwei aufgeteilte ergaben 10 statt 12 und
    // eine negative Korrektur auf abrechenbare Leistung.
    //
    // Nur der ZAEHLERTRAEGER zaehlt (isInspectionCountService — keine Stunden,
    // keine Pauschale): applyInspectionCounter setzt genau ihn auf
    // „Zaehler − Nachweise". Steht der Bericht auf einem Stundenlos, ist er
    // nicht im Stand des Traegers — dann bleibt sein Nachweis im Abzug.
    const traeger = new Set(
        (services || [])
            .filter((s): s is InspectionProofServiceLike => !!s?.id && !proofFlatOf(s))
            .filter((s) => isInspectionCountService({ ...s, flatRate: flatRateField(s) }))
            .map((s) => String(s.id)),
    );
    const mitMenge = new Set<number>();
    for (const entry of serviceLog || []) {
        if (!entry?.serviceId || !traeger.has(entry.serviceId)) continue;
        if ((Number(entry.quantity) || 0) <= 0) continue;
        if (/^(BU|VZ|RN|MB|ALT)-/.test(String(entry.bookingReference ?? ''))) continue;
        const nr = eigeneBerichtsnummer(entry);
        if (nr !== null) mitMenge.add(nr);
    }
    return countProofRefs(flats, serviceLog, (entry) => {
        if (!isOwnReportProofEntry(entry)) return false;
        const nr = eigeneBerichtsnummer(entry);
        return nr === null || !mitMenge.has(nr);
    });
};

/**
 * Nummer des EIGENEN Berichts einer Zeile ('RPT-<abschnitt>-N' ohne CHILD,
 * sonst „Bericht #N abgeschlossen") — null ohne Berichtsbezug. Die Nummer ist
 * projektweit (current_inspection_number), der Abschnitt zaehlt nicht.
 */
const eigeneBerichtsnummer = (entry: InspectionProofLogLike): number | null => {
    const ref = String(entry.reportRef ?? '');
    if (ref.startsWith('RPT-CHILD-')) return null;
    const ausRef = /^RPT-.+-(\d+)$/.exec(ref);
    if (ausRef) return Number(ausRef[1]);
    const ausText = /^Bericht #(\d+) abgeschlossen$/.exec(String(entry.description ?? ''));
    return ausText ? Number(ausText[1]) : null;
};

/**
 * Dieselbe Frage wie inspectionProofCount, je ZEILE: ist diese Protokollzeile
 * eine Begehung, die als Nachweis auf einer Pauschale steht?
 *
 * Fuer die Stellen, die Begehungszeilen einzeln lesen (Datum, Ausfuehrender,
 * Monat) statt sie zu zaehlen. Die Pauschalen werden einmal aufgeloest, der
 * Rueckgabewert laeuft dann ueber das ganze Protokoll.
 */
export const inspectionProofMatcher = (
    services: ProofServices,
): ((entry: InspectionProofLogLike | null | undefined) => boolean) => {
    const flats = proofFlatsById(services);
    if (flats.size === 0) return () => false;
    return (entry) => {
        const flat = entry?.serviceId ? flats.get(entry.serviceId) : undefined;
        return !!flat && isInspectionProofEntry(entry, flat);
    };
};

/**
 * Kind-Projekte werden nie direkt abgerechnet — ihr LV liegt am Elternprojekt.
 * Die Buchhaltungsansicht filtert sie bereits so; der Rechnungslauf und der
 * Projektcheck muessen dieselbe Regel anwenden, sonst entstuenden Rechnungen
 * ueber Positionen, die es am Kind gar nicht gibt.
 */
export const isChildProject = (project: ProjectRowLike | null | undefined): boolean =>
    project?.data?.metadata?.globalData?.linkedProjectConfig?.role === 'child';

/**
 * Verknuepft man ein Projekt als Tochter, gilt ab dann das LV der Mutter
 * (projectHubLogic.resolveEffectiveServices). Ein eigenes LV der Tochter waere
 * damit unsichtbar, bliebe aber in der Datenbank liegen — genau die stille
 * Lage, die bei 26071018/5606 von Hand aufgeloest werden musste. Beim
 * Verknuepfen wandern die Positionen deshalb mit.
 *
 * Diese Funktion plant nur, sie schreibt nichts: der Dialog zeigt den Plan an,
 * bevor irgendetwas passiert.
 */

/** Warum eine Position mit gleicher LV-Nummer NICHT zusammengefuehrt wird. */
export type NoMergeReason =
    /**
     * Die Mutter fuehrt die LV-Nummer MEHRFACH — es ist nicht bestimmt, welche
     * Zeile die Menge aufnehmen wuerde. Siehe die Begruendung an
     * `planLinkTransfer`.
     */
    | 'ambiguous'
    /** Auf die Position der Tochter wurde schon gebucht oder abgerechnet. */
    | 'history'
    /** Die Position IST eine Monatspauschale (traegt die Deckungsregel). */
    | 'flat_rate'
    /**
     * Die Position ist von einer Monatspauschale der TOCHTER gedeckt
     * (`flatCoveredBy`, gestempelt ueber die eigene Liste). Verschmolzen
     * verschwaende ihre `id` — eine 'explicit'-Pauschale verloere damit ihr
     * Ziel (resolveFlatCoverage deckt nur Ids, die es in der Liste gibt), und
     * die gedeckte Menge stuende an der Mutter wieder als offenes Geld da.
     * Bei 'all'/'inspection' deckte die mitgewanderte Pauschale stattdessen
     * die ZEILE DER MUTTER mit — samt deren eigener Menge. Beides verschiebt
     * still Geld; die Position wandert deshalb als eigene Zeile.
     */
    | 'flat_covered'
    /** Auf die Position ist ein Verzicht vermerkt. */
    | 'waiver'
    /**
     * PAKETPOSITION: die Position der Tochter ODER die gleichnamige der Mutter
     * hat Bestandteile (Nr. 165). Verschmolzen ginge der Nachweis verloren —
     * der Merge LOESCHT die Zeile der Tochter, und die Kaskade nimmt alle
     * Bestandteil-Eintraege (Datum, Uhrzeit, Person) mit. Umgekehrt stuende
     * an einem Paket der Mutter nach dem Aufaddieren Menge 2 (Pakete gibt es
     * nur mit Menge 1, E5), und der Deckel gaebe mit dem ersten Paket die
     * zweite Menge frei. Die Position wandert als eigene Zeile; ihre Eintraege
     * ziehen ueber den zusammengesetzten Fremdschluessel mit um.
     */
    | 'bestandteile'
    /** Die beiden Positionen haben verschiedene Einheitspreise. */
    | 'price';

export interface LinkTransferMove {
    /** Position der Tochter, die unveraendert an die Mutter wandert. */
    service: ServiceItem;
    /** Gesetzt, wenn die Mutter dieselbe LV-Nummer fuehrt, aber nicht verschmolzen wird. */
    collidesWith?: ServiceItem;
    /**
     * Wie viele Zeilen der Mutter dieselbe LV-Nummer fuehren. Nur bei
     * `reason: 'ambiguous'` groesser 1 und dort die eigentliche Aussage —
     * `collidesWith` ist dann bloss der erste von mehreren Kandidaten.
     */
    collisionCount?: number;
    reason?: NoMergeReason;
}

export interface LinkTransferMerge {
    /** Position der Tochter — geht in `into` auf und verschwindet. */
    service: ServiceItem;
    /** Position der Mutter, die die Menge aufnimmt. */
    into: ServiceItem;
    /** Beauftragte Menge der Mutter NACH der Zusammenfuehrung. */
    quantityContracted: number;
}

export interface LinkTransferPlan {
    moves: LinkTransferMove[];
    merges: LinkTransferMerge[];
    /** Gesamtzahl betroffener Positionen der Tochter. */
    total: number;
}

/** LV-Nummern vergleichen sich ohne Rand-Leerzeichen und ohne Gross-/Kleinschreibung. */
const normalizeLvPosition = (value: string | undefined | null): string =>
    (value || '').trim().toLowerCase();

/** Eine Position traegt Historie, sobald auf sie gebucht oder abgerechnet wurde. */
const carriesHistory = (service: ServiceItem): boolean =>
    (service.quantityUsed || 0) > 0 || (service.quantityBilled || 0) > 0;

/**
 * Traegt die Position eine PAUSCHALEN-Regel — ist sie selbst die
 * Monatspauschale oder benennt sie als gedeckt?
 *
 * `flatRate` ist keine Menge, sondern eine Abrechnungsregel: sie sagt, welche
 * anderen Positionen geldlich abgegolten sind (ServiceItem.flatRate). Eine
 * solche Position kann fabrikneu sein — Laufzeit vereinbart, noch kein Monat
 * gebucht —, und genau dann traegt sie nach `carriesHistory` NICHTS und waere
 * frueher verschmolzen und geloescht worden. Die Zielposition der Mutter hat
 * die Regel nicht, und `resolveFlatCoverage` findet danach keine Pauschale
 * mehr: die gedeckten Positionen stuenden wieder als offenes Geld da und
 * wuerden ein zweites Mal gefordert.
 *
 * `flatCoveredBy` bleibt hier DRAUSSEN, hat aber seinen EIGENEN Grund
 * (`flat_covered`, siehe NoMergeReason): der Stempel ist abgeleitet
 * (stampFlatCoverage) und entsteht an der Mutter neu — aber nur, wenn die
 * gedeckte Position ihre `id` behaelt. Eine 'explicit'-Pauschale deckt Ids,
 * und eine verschmolzene Id gibt es nicht mehr.
 */
const carriesFlatRate = (service: ServiceItem): boolean => !!service.flatRate;

/**
 * Ist auf der Position ein VERZICHT vermerkt?
 *
 * Geprueft wird auf „es wurde etwas dazu gesagt" (`!== undefined`) und nicht
 * auf „> 0": eine ausdrueckliche 0 ist die RUECKNAHME eines Verzichts und
 * traegt Grund, Zeitpunkt und Person (`waivedReason`/`waivedAt`/`waivedBy`).
 * Diese Spur beim Verschmelzen zu loeschen waere derselbe stille Verlust wie
 * beim Verzicht selbst — und sie kostet nichts: die Position wandert dann eben
 * als eigene Zeile mit, was rechnerisch nichts aendert.
 */
const carriesWaiver = (service: ServiceItem): boolean => service.quantityWaived !== undefined;

/**
 * Plant, was beim Verknuepfen mit den Leistungen der Tochter geschieht.
 *
 * Gleiche LV-Nummer heisst zusammenfuehren — aber nur, solange das keinen
 * Beleg zerreisst, keine Regel unterschlaegt und keinen Preis verbiegt:
 *   • `ambiguous`: die Mutter fuehrt die LV-Nummer MEHRFACH. Das ist kein
 *     Sonderfall, sondern der Normalfall ab der ZWEITEN Tochter: jede
 *     Kollision mit Historie laesst eine zweite Zeile derselben Nummer an der
 *     Mutter zurueck (`reason: 'history'`). Die Reihenfolge, in der diese
 *     Zeilen geladen werden, ist nirgends festgelegt — `loadProjectsFromCloud`
 *     holt `project_services` als verschachteltes Select ohne `order`. Welche
 *     Zeile die Menge aufnaehme, waere damit Zufall, und ein falsch
 *     aufgenommener Auftragswert faellt niemandem auf. Also wird nicht
 *     geraten: die Position wandert als eigene Zeile mit, und der Dialog nennt
 *     sie.
 *   • `history`: auf die Position der Tochter wurde bereits gebucht oder
 *     abgerechnet. Ihre `id` steht in Berichtsbuchungen und Rechnungszeilen
 *     (lines[].serviceId). Verschwaende sie, zeigten diese Bezuege ins Leere
 *     und der Leistungsnachweis der Rechnung verloere seinen Anker. Sie wandert
 *     deshalb als eigene Position mit — die `id` bleibt erhalten.
 *   • `flat_rate` / `flat_covered` / `waiver`: die Position traegt eine
 *     Monatspauschale, ist von einer gedeckt oder traegt einen Verzicht.
 *     Alles REGELN und keine Mengen; `carriesHistory` sieht sie nicht,
 *     solange noch nichts gebucht ist. Siehe `carriesFlatRate`,
 *     `NoMergeReason.flat_covered` und `carriesWaiver`.
 *   • `bestandteile`: eine der beiden Positionen ist eine Paketposition mit
 *     Bestandteilen — verschmolzen gingen die Eintraege (der Nachweis) mit
 *     der geloeschten Zeile verloren. Siehe `NoMergeReason.bestandteile`.
 *   • `price`: verschiedene Einheitspreise. Zusammengefuehrt gaebe es fuer die
 *     gemeinsame Menge nur noch einen Preis, der Auftragswert aenderte sich
 *     still.
 * In allen Faellen nennt der Dialog die Position beim Namen, damit sie von
 * Hand geklaert werden kann.
 */
export const planLinkTransfer = (
    childServices: ServiceItem[] | undefined | null,
    parentServices: ServiceItem[] | undefined | null,
): LinkTransferPlan => {
    // Die Deckung wird HIER gestempelt und nicht vom Aufrufer erwartet —
    // dieselbe Begruendung wie in planLinkReturn: eine Regel, die auf einen
    // fremden Stempel angewiesen ist, ist bei der ersten neuen Aufrufstelle
    // still ausser Kraft. Der Stempel ist rein abgeleitet und wird nirgends
    // persistiert; die Objekte im Plan tragen dieselben ids wie vorher.
    const children = stampFlatCoverage(childServices || []);
    const parents = parentServices || [];
    const moves: LinkTransferMove[] = [];
    const merges: LinkTransferMerge[] = [];
    // Laufende Zielmenge je Mutter-Position: fuehrt die Tochter zwei Zeilen mit
    // derselben LV-Nummer, addieren sich beide auf dieselbe Position.
    const contractedByTarget = new Map<string, number>();

    for (const service of children) {
        const lv = normalizeLvPosition(service.lvPosition);
        // Ohne LV-Nummer gibt es keine Kollision — solche Zeilen wandern immer.
        // Es wird bewusst die VOLLE Trefferliste geholt und nicht der erste
        // Treffer: die Anzahl ist die Aussage, nicht der Treffer.
        const matches = lv ? parents.filter(p => normalizeLvPosition(p.lvPosition) === lv) : [];
        const match = matches[0];

        if (!match) {
            moves.push({ service });
        } else if (matches.length > 1) {
            // `collidesWith` ist hier nur EIN Vertreter der Mehrdeutigkeit —
            // der Dialog nennt deshalb die Anzahl, nicht diese eine Zeile.
            moves.push({ service, collidesWith: match, collisionCount: matches.length, reason: 'ambiguous' });
        } else if (hatBestandteile(service) || hatBestandteile(match)) {
            // PAKETPOSITION auf EINER der beiden Seiten — vor `history`, weil
            // das der genauere Grund ist (auch eine fabrikneue Paketposition
            // ohne jede Buchung darf nicht verschmelzen). Begruendung an
            // NoMergeReason 'bestandteile'; applyLinkTransfer haelt denselben
            // Riegel als zweites Netz.
            moves.push({ service, collidesWith: match, reason: 'bestandteile' });
        } else if (carriesHistory(service)) {
            moves.push({ service, collidesWith: match, reason: 'history' });
        } else if (carriesFlatRate(service)) {
            moves.push({ service, collidesWith: match, reason: 'flat_rate' });
        } else if (service.flatCoveredBy) {
            moves.push({ service, collidesWith: match, reason: 'flat_covered' });
        } else if (carriesWaiver(service)) {
            moves.push({ service, collidesWith: match, reason: 'waiver' });
        } else if ((service.unitPrice || 0) !== (match.unitPrice || 0)) {
            moves.push({ service, collidesWith: match, reason: 'price' });
        } else {
            const base = contractedByTarget.get(match.id) ?? (match.quantityContracted || 0);
            const quantityContracted = base + (service.quantityContracted || 0);
            contractedByTarget.set(match.id, quantityContracted);
            merges.push({ service, into: match, quantityContracted });
        }
    }

    return { moves, merges, total: moves.length + merges.length };
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * VERGLEICHSBILD EINES PLANS — „ist das noch derselbe Plan?"
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Beide Dialoge (Verknuepfen wie Trennen) zeigen einen Plan, der aus einem
 * GELADENEN Stand des Mutterprojekts gerechnet ist. Zwischen dem Anzeigen und
 * dem Bestaetigen koennen Minuten liegen; in der Zeit kann an der Mutter
 * gebucht, abgerechnet oder eine Position angelegt worden sein. Beide Dialoge
 * rechnen den Plan deshalb unmittelbar vor dem Ausfuehren aus frischem Stand
 * neu — und brauchen dann die Antwort auf genau eine Frage: ist das noch der
 * Plan, den der Anwender gesehen und bestaetigt hat?
 *
 * Verglichen wird NICHT auf Objektgleichheit (die frischen Objekte sind immer
 * andere) und auch nicht der volle Inhalt: eine an der Mutter gebuchte Menge
 * aendert `quantityUsed`, aber weder was geschieht noch warum. Wuerde sie
 * mitzaehlen, verlangte jeder Dialog nach jeder fremden Buchung eine zweite
 * Bestaetigung, und die Rueckfrage waere binnen einer Woche eine Floskel, die
 * niemand mehr liest.
 *
 * Aufgenommen wird deshalb nur, was die AUSFUEHRUNG STEUERT oder was der
 * Anwender zur Entscheidung braucht. Der Vergleich ist eine reine Funktion und
 * liegt hier bei den Planfunktionen, damit beide Dialoge dasselbe Bild
 * benutzen: zwei Fassungen derselben Regel laufen frueher oder spaeter
 * auseinander.
 */

/**
 * Verknuepfen: welche Position wohin verschmilzt (samt Zielmenge — sie wird
 * geschrieben) und welche als eigene Zeile wandert (samt Grund — er steht im
 * Dialog).
 */
export const linkTransferPlanFingerprint = (plan: LinkTransferPlan): string =>
    JSON.stringify({
        merges: plan.merges.map(m => [m.service.id, m.into.id, m.quantityContracted]),
        moves: plan.moves.map(m => [m.service.id, m.reason || null]),
    });

/**
 * Trennen: welche Positionen zurueckgehen, welche zurueckgehalten werden und
 * WARUM — samt der Belege, an denen ein Halt haengt (sie stehen im Dialog und
 * sind die einzige Spur zur Rechnung der Mutter).
 *
 * `unstamped` zaehlt mit, obwohl es keine Ausfuehrung steuert: die Zahl steht
 * als ausdrueckliche Auskunft im Dialog („so viele Positionen der Mutter
 * tragen keinen Herkunftsvermerk"). Aendert sie sich, hat sich das LV der
 * Mutter veraendert, und der Anwender bestaetigt sonst eine Auskunft, die
 * nicht mehr stimmt.
 *
 * Sortiert, weil die Reihenfolge aus der LV-Sortierung der Mutter stammt und
 * nicht aus einer Entscheidung: eine umbenannte LV-Nummer verschoebe sonst
 * Zeilen und meldete eine Aenderung, die keine ist.
 */
export const linkReturnPlanFingerprint = (plan: LinkReturnPlan): string =>
    JSON.stringify({
        moves: plan.moves.map(m => m.service.id).sort(),
        holds: plan.holds
            .map(h => [h.service.id, h.reason, [...(h.bookingReferences || [])].sort().join('|')])
            .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
        unstamped: plan.unstamped,
        notes: [...plan.notes].sort(),
    });

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE PAUSCHALE WIRD BEIM UMZUG FESTGESCHRIEBEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ─── Der stille Geldverlust, gegen den das gebaut ist ──────────────────────
 * Eine Monatspauschale (`ServiceItem.flatRate`) traegt einen `scope`. Bei
 * 'explicit' zeigt sie auf ausdrueckliche Ids; bei 'inspection' und 'all' ist
 * die Deckung dagegen PRAEDIKATBASIERT — `resolveFlatCoverage` laeuft ueber
 * die VOLLE Positionsliste und deckt alles, was auf das Praedikat passt.
 *
 * Solange die Pauschale bei der Tochter liegt, ist diese Liste das LV der
 * Tochter. Wandert sie beim Verknuepfen an die Mutter, ist es ab dem naechsten
 * Laden das LV der MUTTER — und die Pauschale der Tochter deckt dort ploetzlich
 * auch die EIGENEN Begehungspositionen der Mutter. Deren offene Mengen tragen
 * dann `flatCoveredBy`, fallen aus `displayOpen` heraus und werden NIE MEHR
 * abgerechnet. Niemand sieht es: es steht kein Fehler, keine Warnung, nur eine
 * Rechnung, die weniger fordert als sie duerfte. Eine Pauschale von 1.200 EUR
 * der Tochter bezahlt still die Begehungen der Mutter mit.
 *
 * ─── Die Antwort: aus dem Praedikat werden Ids ─────────────────────────────
 * Beim Umzug wird der scope auf 'explicit' umgeschrieben, mit genau den Ids,
 * die die Pauschale VOR dem Umzug gedeckt hat — aufgeloest gegen die Liste der
 * QUELLE, nicht gegen die des Ziels. Die Deckung bleibt damit exakt dieselbe
 * Menge Geld wie vorher, und sie kann am Ziel nicht weiterwachsen.
 *
 * Eine Pauschale, die vorher NICHTS gedeckt hat, kommt mit einer leeren Liste
 * an. Das ist kein Verlust, sondern dieselbe Aussage: sie deckte nichts.
 * Waere sie mit 'all' weitergereist, deckte sie am Ziel schlagartig alles.
 *
 * 'explicit' bleibt UNVERAENDERT. Sie zeigt schon auf Ids, streut nicht und
 * darf nicht angefasst werden: eine Id, die der Anwender bewusst eingetragen
 * hat, ist eine Angabe und keine Ableitung.
 *
 * ─── Ueberleben die Ids den Umzug? Nachgewiesen, nicht angenommen ──────────
 * Eine Id verschwindet beim Umzug nur an EINER Stelle: beim MERGE (die Zeile
 * der Tochter geht in der Zeile der Mutter auf und wird geloescht). Zeigte die
 * eingefrorene Pauschale auf eine verschmolzene Id, fande `resolveFlatCoverage`
 * sie nicht mehr ('explicit' deckt nur Ids, die es in der Liste GIBT) — die
 * gedeckte Menge stuende an der Mutter wieder als offenes Geld da.
 *
 * Das kann nicht eintreten, und zwar nachweisbar aus `planLinkTransfer`:
 * `stampFlatCoverage` laeuft dort ueber die volle Tochterliste, BEVOR
 * entschieden wird; jede gedeckte Position traegt danach `flatCoveredBy` und
 * faellt auf `reason: 'flat_covered'` — und jeder `reason` ist ein MOVE. Die
 * Zweige VOR 'flat_covered' ('ambiguous', 'bestandteile', 'history', 'flat_rate') sind
 * ebenfalls Moves; der Merge-Zweig steht dahinter und ist fuer eine gedeckte
 * Position unerreichbar. Eine gedeckte Position wandert also immer als eigene
 * Zeile und behaelt ihre `id`.
 *
 * `leftBehind` haelt den Nachweis trotzdem fest, statt sich darauf zu
 * verlassen: bleibt eine gedeckte Position wider Erwarten zurueck, sagt der
 * Plan es, und der Schreibweg bricht ab, statt Geld zu verschieben. Eine
 * Annahme, die nur im Kommentar steht, ist beim naechsten Umbau still weg.
 *
 * ─── Die Rueckrichtung ─────────────────────────────────────────────────────
 * Beim TRENNEN stellt sich dieselbe Frage — und `planLinkReturn` beantwortet
 * sie, indem eine Pauschale ueberhaupt nicht zurueckgeht (`reason:
 * 'flat_rate'`), ebenso wenig eine gedeckte Position ('flat_covered'). Es gibt
 * dort also nichts umzuschreiben. `applyLinkReturn` prueft das trotzdem, aus
 * demselben Grund wie `leftBehind` hier.
 */
export interface FlatScopeFreeze {
    /** Die wandernde Pauschalposition. */
    service: ServiceItem;
    /**
     * Die Deckungsregel NACH dem Umzug — immer `scope: 'explicit'` mit den
     * aufgeloesten Ids. Genau dieses Objekt gehoert in `project_services
     * .flat_rate`, und zwar im SELBEN `update`, das die Position umhaengt.
     */
    flatRate: FlatRateCoverage;
    /**
     * Ids, die die Pauschale vor dem Umzug gedeckt hat, die aber NICHT
     * mitwandern. Immer leer, solange `planLinkTransfer` gedeckte Positionen
     * ueber 'flat_covered' vom Merge fernhaelt — siehe oben. Ist sie es
     * nicht, darf nicht geschrieben werden.
     */
    leftBehind: string[];
}

/**
 * Rechnet aus, wie die `flatRate` der wandernden Pauschalen nach dem Umzug
 * aussehen muss. Rein, ohne Datenbank — die ganze Begruendung steht oben.
 *
 * `sourceServices` ist die VOLLE Positionsliste der Quelle in ihrer
 * urspruenglichen Reihenfolge (die Deckung wird gegen sie aufgeloest, nicht
 * gegen die des Ziels). `movingServiceIds` sind die Positionen, die ihre `id`
 * behalten und ankommen — beim Verknuepfen also die MOVES, nicht die Merges.
 */
export const planFlatScopeFreeze = (
    sourceServices: ServiceItem[] | null | undefined,
    movingServiceIds: Iterable<string> | null | undefined,
): FlatScopeFreeze[] => {
    const list = sourceServices || [];
    const moving = new Set(movingServiceIds || []);
    if (list.length === 0 || moving.size === 0) return [];

    // Dieselbe Aufloesung, die auch `stampFlatCoverage` benutzt — keine zweite
    // Fassung der Deckungsregel. Insbesondere gilt hier dieselbe
    // Gewinner-Regel bei zwei Pauschalen: die erste in Listenreihenfolge.
    const coverage = resolveFlatCoverage(list);
    const freezes: FlatScopeFreeze[] = [];

    for (const service of list) {
        if (!moving.has(service.id)) continue;
        const scope = service.flatRate?.scope;
        // NUR die praedikatbasierten Formen. 'explicit' bleibt unveraendert
        // (sie streut nicht), und ein `flatRate` ohne brauchbaren scope ist
        // keine Pauschale (isFlatRateService) — daraus eine zu machen waere
        // schlimmer als sie zu lassen.
        if (scope !== 'inspection' && scope !== 'all') continue;

        const serviceIds: string[] = [];
        const leftBehind: string[] = [];
        coverage.forEach((flatId, coveredId) => {
            if (flatId !== service.id) return;
            (moving.has(coveredId) ? serviceIds : leftBehind).push(coveredId);
        });

        freezes.push({ service, flatRate: { scope: 'explicit', serviceIds }, leftBehind });
    }

    return freezes;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DAS LEISTUNGSPROTOKOLL WANDERT MIT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `applyLinkTransfer` haengt die POSITIONEN um (`project_services.project_id`).
 * Die zugehoerigen Protokolleintraege liegen aber nicht dort, sondern im
 * JSON-Blob der Tochter (`data.metadata.globalData.serviceLog`) — und blieben
 * bis hierher liegen. Das Ergebnis war jedes Mal dasselbe Bild: an der Mutter
 * steht `quantityUsed = 5` und es gibt keinen einzigen Eintrag, der die Menge
 * belegt. Der Projektcheck meldet das als `unlogged_quantity` ueber die VOLLE
 * Menge (projectCheckLogic), und der Leistungsnachweis einer Rechnung findet
 * fuer die Position keinen Anker mehr.
 *
 * Diese Funktion rechnet nur — welche Eintraege wandern und mit welcher
 * `serviceId` sie an der Mutter ankommen. Geschrieben wird in
 * cloudService.applyLinkTransfer.
 *
 * ─── Die beiden Faelle ─────────────────────────────────────────────────────
 * MOVE: die Position behaelt ihre `id` (sie zieht per `update` um, nie per
 * Neuanlage). Der Eintrag wandert damit voellig unveraendert — nur eben in ein
 * anderes JSON.
 *
 * MERGE: die Zeile der Tochter VERSCHWINDET, ihre Menge geht in die Zeile der
 * Mutter auf. Ein Eintrag, der auf die verschwindende `id` zeigt, zeigte
 * danach ins Leere; er wird deshalb auf `merge.into.id` umgeschrieben.
 *
 * ─── Kann es beim MERGE ueberhaupt Eintraege geben? ────────────────────────
 * Ja — deshalb steht der Code hier und nicht nur eine Begruendung.
 * Verschmolzen wird nur ohne Historie (`carriesHistory`: `quantityUsed` und
 * `quantityBilled` beide 0), und das schliesst Protokolleintraege NICHT aus:
 *   • Eine Ruecknahme (`serviceReversalService`) schreibt eine Gegenzeile mit
 *     negativer Menge und setzt `quantityUsed` zurueck auf 0. Buchung und
 *     Gegenbuchung bleiben als ZWEI Zeilen stehen, die sich zu 0 saldieren.
 *   • Ebenso die Ruecknahme einer manuellen Verbuchung (`manualBillingService`
 *     — ausdruecklich ein Anhang mit negativer Menge, kein Loeschen).
 *   • Zeilen mit Menge 0 (reine Vermerke) zaehlen ohnehin in keinen Stand.
 * Genau diese Zeilen sind der Nachweis, dass etwas zurueckgenommen wurde. Sie
 * an einer geloeschten `id` haengen zu lassen hiesse, den Nachweis der
 * Ruecknahme zu verlieren.
 *
 * ─── Was NICHT umgeschrieben wird ──────────────────────────────────────────
 * Nur `serviceId`. Die `id` des Eintrags bleibt, weil Zuordnungen von
 * Rechnungen darauf zeigen (`allocations.entryId`) und weil die Ruecknahme im
 * Buchungsjournal ueber genau diese id aufloest (`voidJournalEntries`).
 */
export interface ServiceLogTransfer {
    /**
     * Eintraege fuer die Mutter — bei einem Merge bereits auf die Ziel-`id`
     * umgeschrieben, sonst die Originalobjekte.
     */
    moving: ServiceTransaction[];
    /** Was an der Tochter bleibt (alles, was zu keiner wandernden Position gehoert). */
    staying: ServiceTransaction[];
    /**
     * Die urspruenglichen Eintrags-`id`s der wandernden Zeilen, in derselben
     * Reihenfolge wie `moving`. Das Buchungsjournal loest seine Zeilen ueber
     * diese Werte auf; nach dem Umschreiben der `serviceId` waeren sie sonst
     * nur noch ueber einen zweiten Abgleich zu finden.
     */
    movedEntryIds: string[];
}

/**
 * Die Tochter, von der die Zeilen kommen — beim HINWEG. Ohne diese Angabe ist
 * der Aufruf der RUECKWEG (planLinkReturnLog), und die Zeilen werden in ihre
 * urspruengliche Form zurueckgebracht.
 */
export interface LogTransferOrigin {
    projectId: string;
    projectName?: string;
    projectNumber?: string;
    /** ISO-Zeitstempel des Umzugs. */
    at: string;
}

/**
 * Eine Zeile beim HINWEG in die Kind-Form bringen.
 *
 * An der Mutter darf eine Tochter-Begehung nicht wie eine eigene aussehen:
 * `Bericht #8 abgeschlossen` mit `RPT-all-8` hiesse dort „Bericht 8 der
 * Mutter", und genau so las der Leistungsnachweis es — Berichtsnummern sind
 * nur innerhalb eines Projekts eindeutig. Geschrieben wird die Form, die der
 * Kind-zu-Mutter-Buchungspfad seit je schreibt (childBookingRef /
 * childBookingDescription); Journal, Tagesregel und Nachweis erkennen sie.
 *
 * Zwei Grade, weil die Beschreibung an anderen Stellen ausgewertet wird:
 *   • HAUPTBUCHUNG (`Bericht #N abgeschlossen`): Ref UND Text in die Kind-Form.
 *   • Jede andere Zeile mit Berichtsbezug (Zusatzleistung, Versandvermerk):
 *     NUR der Ref. Der Text bleibt — `Zusatzleistung: …` ist die Aussage, an
 *     der `is_addon` haengt, und ein Praefix davor machte aus der Zusatz-
 *     leistung eine gedeckte Begehung.
 *   • Ohne Berichtsbezug (Vorankuendigung, Pauschale): nichts umschreiben,
 *     nur die Herkunft vermerken.
 *
 * Das Original wird in `transferredFrom` festgehalten, damit der Rueckweg es
 * zeichengleich wiederherstellt — der Abschnitt im urspruenglichen Ref ist in
 * der Kind-Form nicht enthalten und liesse sich sonst nicht zurueckrechnen.
 */
const inKindForm = (
    entry: ServiceTransaction,
    origin: LogTransferOrigin,
    einheit: string,
): ServiceTransaction => {
    const nummer = reportNumberOf(entry);
    const herkunft: NonNullable<ServiceTransaction['transferredFrom']> = {
        projectId: origin.projectId,
        projectName: origin.projectName,
        projectNumber: origin.projectNumber,
        at: origin.at,
        reportRef: typeof entry.reportRef === 'string' ? entry.reportRef : null,
        description: String(entry.description ?? ''),
    };
    // `ohneBuchungsart`: die Zeile wandert in ein anderes Projekt und wird
    // dabei umgeschrieben (Ref, bei der Hauptbuchung auch der Text). Eine aus
    // dem Journal mitgebrachte Buchungsart beschriebe die Zeile VOR dem Umzug;
    // die neue leitet `journalKind` beim Schreiben aus dem Inhalt ab.
    if (nummer === null) return { ...ohneBuchungsart(entry), transferredFrom: herkunft };
    const umgeschrieben: ServiceTransaction = {
        ...ohneBuchungsart(entry),
        reportRef: childBookingRef(origin.projectId, nummer),
        transferredFrom: herkunft,
    };
    if (isMainInspectionBooking(entry)) {
        umgeschrieben.description = childBookingDescription(
            origin.projectName || origin.projectNumber || origin.projectId,
            nummer,
            entry.quantity || 0,
            einheit,
        );
    }
    return umgeschrieben;
};

/**
 * Eine Zeile beim RUECKWEG in ihre urspruengliche Form bringen.
 *
 * Nur, wenn die Herkunft festgehalten wurde. Eine Zeile ohne
 * `transferredFrom` ist entweder nie gewandert (dann gibt es nichts
 * zurueckzudrehen) oder aus der Zeit vor diesem Umbau — dann bleibt sie, wie
 * sie ist; raten, was sie vorher trug, kann niemand.
 */
const inEigeneForm = (entry: ServiceTransaction): ServiceTransaction => {
    const herkunft = entry.transferredFrom;
    if (!herkunft) return entry;
    const { transferredFrom: _weg, ...rest } = entry;
    // Derselbe Grund wie auf dem Hinweg: Text und Ref werden zurueckgesetzt,
    // die Buchungsart darf die alte Form nicht ueberleben.
    const zurueck: ServiceTransaction = { ...ohneBuchungsart(rest), description: herkunft.description };
    if (herkunft.reportRef) zurueck.reportRef = herkunft.reportRef;
    else delete zurueck.reportRef;
    return zurueck;
};

export const planServiceLogTransfer = (
    plan: LinkTransferPlan,
    childLog: ServiceTransaction[] | undefined | null,
    /** Beim Hinweg die Tochter; weggelassen = Rueckweg. */
    origin?: LogTransferOrigin | null,
): ServiceLogTransfer => {
    const log = Array.isArray(childLog) ? childLog : [];
    // Ziel-id je betroffener Tochter-Position. Beim Move ist sie die eigene,
    // beim Merge die der Mutter.
    const targetByServiceId = new Map<string, string>();
    const unitByServiceId = new Map<string, string>();
    for (const move of plan.moves) {
        targetByServiceId.set(move.service.id, move.service.id);
        unitByServiceId.set(move.service.id, move.service.unit || '');
    }
    for (const merge of plan.merges) {
        targetByServiceId.set(merge.service.id, merge.into.id);
        unitByServiceId.set(merge.service.id, merge.into.unit || merge.service.unit || '');
    }

    const moving: ServiceTransaction[] = [];
    const staying: ServiceTransaction[] = [];
    const movedEntryIds: string[] = [];

    for (const entry of log) {
        // Defensiv: eine Zeile ohne `serviceId` gehoert zu keiner Position und
        // bleibt, wo sie ist. Sie waere an der Mutter nicht zuzuordnen.
        const serviceId = typeof entry?.serviceId === 'string' ? entry.serviceId : '';
        const target = serviceId ? targetByServiceId.get(serviceId) : undefined;
        if (!target) {
            staying.push(entry);
            continue;
        }
        movedEntryIds.push(String(entry.id ?? ''));
        const umgehaengt = target === serviceId ? entry : { ...entry, serviceId: target };
        if (origin) {
            moving.push(inKindForm(umgehaengt, origin, unitByServiceId.get(serviceId) || ''));
            continue;
        }
        const zurueck = inEigeneForm(umgehaengt);
        // Unveraendert weiterreichen, wo nichts zu aendern ist: so bleibt die
        // Objektidentitaet erhalten und ein Vergleich im Aufrufer sieht, dass
        // wirklich nichts angefasst wurde.
        moving.push(zurueck === umgehaengt ? umgehaengt : zurueck);
    }

    return { moving, staying, movedEntryIds };
};

/**
 * Beschriftung der HERKUNFT einer gewanderten Position — oder `null`, wenn sie
 * immer schon dort lag (der Normalfall).
 *
 * Der Fall, fuer den es das gibt: eine Mutter mit mehreren Toechtern fuehrt
 * mehrere Zeilen derselben LV-Nummer (jede Kollision mit Historie laesst eine
 * eigene zurueck). Ohne die Herkunft sind sie im Buchungsdialog nicht zu
 * unterscheiden, und eine Begehung wird auf die Position der Schwester
 * gebucht.
 *
 * Bewusst OHNE Preis und ohne Menge: die Angabe muss auch dort stehen duerfen,
 * wo `canSeeFinancials` falsch ist (Buchungsdialog des Aussendiensts) — sie
 * beantwortet die Frage „welche der gleichnamigen Zeilen ist meine", nicht
 * „was kostet sie".
 */
export const transferOriginLabel = (service: ServiceItem): string | null => {
    const origin = service.transferredFrom;
    if (!origin) return null;
    const bezeichnung = [origin.projectNumber, origin.projectName].filter(Boolean).join(' · ');
    // Ohne Nummer und ohne Namen bleibt die Aussage trotzdem stehen: DASS die
    // Position von woanders kam, ist die Information, die vor der Fehlbuchung
    // schuetzt. Die id gehoert nicht in die Oberflaeche.
    return bezeichnung ? `Übernommen aus ${bezeichnung}` : 'Aus einem Tochterprojekt übernommen';
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE RUECKFAHRKARTE — was beim TRENNEN an die Tochter zurueckgeht
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Verknuepfen ist ein Umbau (`planLinkTransfer` + `applyLinkTransfer`),
 * Trennen war bis hierher ein Schalter: `linkedProjectConfig` geloescht, und
 * die Positionen blieben bei der Mutter liegen. Die Tochter stand danach OHNE
 * LV da — sie konnte nichts mehr buchen, ihre Kacheln zaehlten nichts, und
 * ihre erbrachten Leistungen standen weiter im LV der Mutter, wo sie niemand
 * mehr zuordnen konnte. Diese Asymmetrie war der Fehler.
 *
 * Diese Funktion plant die Umkehrung. Sie rechnet nur — der Dialog zeigt den
 * Plan, BEVOR etwas passiert, genau wie beim Verknuepfen.
 *
 * ─── Woran die Rueckfahrkarte haengt ───────────────────────────────────────
 * Allein an `ServiceItem.transferredFrom` (SQL Nr. 68). Zurueck geht NUR, was
 * genau diese Tochter ausweist. Alles andere gehoert der Mutter und wird nie
 * angefasst — auch nicht "wahrscheinlich": eine falsch mitgenommene Position
 * nimmt der Mutter ihr Geld und faellt erst bei der naechsten Rechnung auf.
 *
 * ─── Was NICHT zurueckgeht, und warum ──────────────────────────────────────
 * 1. ALTBESTAND ohne Vermerk. Umzuege vor Nr. 68 haben keine Spur hinterlassen
 *    (das Skript sagt es ausdruecklich: kein Backfill, nichts wird geraten).
 *    Eine Position ohne `transferredFrom` ist von einer Position, die immer
 *    schon der Mutter gehoerte, nicht zu unterscheiden. Sie bleibt — und der
 *    Dialog SAGT das, statt es zu verschweigen (`unstamped`, Note
 *    'unstamped_parent_services'). Wer die Zeile kennt, kann sie von Hand
 *    ordnen; wer nichts davon erfaehrt, sucht sie nie.
 * 2. VERSCHMOLZENE Mengen. `applyLinkTransfer` erhoeht beim Merge nur
 *    `quantity_contracted` der Mutterzeile und LOESCHT die Zeile der Tochter —
 *    es bleibt keinerlei Spur, welcher Anteil der gemeinsamen Menge einmal der
 *    Tochter gehoerte. Diese Mengen bleiben deshalb bei der Mutter; geraten
 *    wird nichts (Note 'merged_untraceable'). Dieselbe blinde Stelle wirkt in
 *    die andere Richtung: hat eine SCHWESTER ihre Menge in eine Position
 *    verschmolzen, die diese Tochter ausweist, wandert sie mit zurueck. Auch
 *    das steht in der Note — eine unsichtbare Unsicherheit ist die schlimmere.
 * 3. Positionen, auf die SEIT DEM UMZUG an der Mutter ABGERECHNET wurde
 *    (`billed_at_parent`). Dazu unten die ausfuehrliche Begruendung.
 * 4. Positionen, an denen eine MONATSPAUSCHALE haengt (`flat_rate`) oder die
 *    von einer gedeckt sind (`flat_covered`) — beides Geldregeln der Mutter,
 *    siehe die Begruendung an den Reasons.
 */
export type NoReturnReason =
    /**
     * Seit dem Umzug hat eine Rechnung der MUTTER diese Position gefordert.
     *
     * Technisch koennte sie zurueck: sie wandert als DIESELBE Zeile (`update`,
     * nie Neuanlage), ihre `id` bleibt, und die Rechnungszeile der Mutter
     * (`lines[].serviceId`) zeigte weiterhin auf eine existierende Zeile. Der
     * eingefrorene Beleg wird ohnehin nie umgeschrieben.
     *
     * Trotzdem geht sie NICHT zurueck, aus zwei Gruenden, die beide schon
     * einmal Geld gekostet haben:
     *
     *   • `assertServicesExist` (invoiceWriteBackLogic) prueft die Zeilen eines
     *     Belegs gegen die Leistungen SEINES Projekts und wirft, sobald eine
     *     `serviceId` dort fehlt. Der Beleg der Mutter liesse sich damit nicht
     *     mehr stornieren und nicht mehr gutschreiben — genau der Vorfall
     *     RE-26081024, bei dem ein Storno an einer nach dem Ausstellen
     *     umgezogenen Position abbrach.
     *   • Abgerechnet wird KUMULATIV (Gesamtstand minus Vorrechnungen). Faellt
     *     die Position aus dem LV der Mutter, schrumpft ihr Gesamtstand,
     *     waehrend der Abzug der Vorrechnungen voll stehen bleibt — die
     *     naechste Rechnung der Mutter rechnete gegen sich selbst.
     *
     * Massgeblich ist der ZEITPUNKT: nur ein Beleg SEIT dem Umzug gehoert der
     * Mutter. Eine Position, die ihre Abrechnung aus der Zeit VOR dem Umzug
     * mitgebracht hat (die Tochter war damals ein eigenstaendiges Projekt mit
     * eigenen Rechnungen), muss zurueck — sonst stuende ihr eigener Beleg nach
     * dem Trennen vor demselben Problem, nur spiegelverkehrt.
     *
     * Dass die Tochter nach dem Trennen selbst abrechnen darf (die Pruefregel
     * `child_project_direct` greift dann nicht mehr), aendert daran nichts:
     * der Beleg der Mutter existiert weiter und braucht seine Zeile.
     */
    | 'billed_at_parent'
    /**
     * An der Position haengt eine MONATSPAUSCHALE (`flatRate`) — eine
     * Geldregel, die sagt, welche ANDEREN Positionen abgegolten sind. Die
     * gedeckten Positionen stehen bei der Mutter. Nimmt man die Pauschale
     * mit, findet `resolveFlatCoverage` sie dort nicht mehr, und alles, was
     * sie bisher abgegolten hat, steht wieder als offenes Geld da und wird ein
     * zweites Mal gefordert. Dieselbe Ueberlegung wie `carriesFlatRate` beim
     * Verknuepfen, nur in die andere Richtung.
     */
    | 'flat_rate'
    /**
     * Die Position ist von einer Pauschale der MUTTER gedeckt
     * (`flatCoveredBy`). Ihre offene Menge ist mit der Pauschale bezahlt; bei
     * der Tochter waere sie wieder offenes Geld — dieselbe Leistung zweimal
     * gefordert.
     */
    | 'flat_covered';

export interface LinkReturnMove {
    /** Position der Mutter, die als DIESELBE Zeile an die Tochter zurueckgeht. */
    service: ServiceItem;
    /** Der Vermerk, der sie ausweist — die Rueckfahrkarte selbst. */
    origin: ServiceTransferOrigin;
}

export interface LinkReturnHold {
    service: ServiceItem;
    origin: ServiceTransferOrigin;
    reason: NoReturnReason;
    /**
     * Die Belege, an denen die Position bei der Mutter haengt — nur bei
     * `billed_at_parent` und dort die eigentliche Auskunft: mit der
     * Buchungsreferenz laesst sich die Rechnung finden.
     */
    bookingReferences?: string[];
}

/**
 * Was der Plan ueber den Rest sagen muss, obwohl er ihn nicht auflisten kann.
 * Beides sind BLINDE STELLEN des Umzugs, keine Befunde an einzelnen Zeilen.
 */
export type LinkReturnNote = 'unstamped_parent_services' | 'merged_untraceable';

export interface LinkReturnPlan {
    /** Geht zurueck an die Tochter. */
    moves: LinkReturnMove[];
    /** Weist die Tochter aus, bleibt aber bei der Mutter — mit Grund. */
    holds: LinkReturnHold[];
    /** Positionen, die diese Tochter ueberhaupt ausweisen. */
    total: number;
    /**
     * Wie viele Positionen der Mutter GAR KEINEN Herkunftsvermerk tragen —
     * ihre eigenen und der nicht mehr aufloesbare Altbestand zusammen. Der
     * Dialog nennt die Zahl; die Aussage ist "hier koennte noch etwas von
     * dieser Tochter liegen, und niemand kann es feststellen".
     */
    unstamped: number;
    notes: LinkReturnNote[];
}

/**
 * Traegt die Protokollzeile eine ABRECHNUNG (und nicht eine Leistung)?
 *
 * Die Klassifizierung ist die des Bestands: `BU-` entsteht beim Ausstellen
 * einer Rechnung (planWriteBack legt je Leistung genau einen Abdruck unter der
 * Buchungsreferenz ab), `ALT-` beim Einlesen einer Alt-Rechnung. Ohne Referenz
 * (oder mit `LEGACY-AUTO`) ist die Zeile erbrachte Leistung und sagt nichts
 * ueber einen Beleg.
 *
 * Die uebrigen Buchhaltungs-Praefixe (serviceLogUtils.isAccountingEntry)
 * bleiben BEWUSST draussen — hinter keinem steht ein Dokument der Mutter,
 * dessen Zeile die Position hier halten muesste:
 *   • `MB-` (manuelle Verbuchung) ist ausdruecklich KEIN Beleg — jemand hat
 *     eine anderswo abgerechnete Menge von Hand gestempelt. Zeile,
 *     `quantityBilled` und Position wandern als Einheit zurueck; auf beiden
 *     Seiten bleibt der Stand in sich stimmig.
 *   • `VZ-` (Verzicht) und `RN-` (Ruecknahme) sind Vermerke mit Menge 0;
 *     Verzicht und Ruecknahme stehen an der Position selbst und reisen mit.
 *
 * Bewusst NICHT auf die Journal-Buchungsart umgestellt: `BU-` und `MB-` teilen
 * sich dort `manual_posting`, der Beleg-Unterschied ist im kind nicht
 * abbildbar.
 */
const isBillingEntry = (entry: ServiceTransaction): boolean =>
    /^(BU|ALT)-/i.test((entry?.bookingReference || '').trim());

/**
 * Zeitpunkt einer Protokollzeile: der Buchungszeitpunkt, sonst das
 * Leistungsdatum.
 *
 * Unlesbares gilt als "danach" — eine Zeile, deren Zeitpunkt sich nicht
 * bestimmen laesst, darf eine Position nicht freigeben. In dieser Richtung
 * kostet der Irrtum eine Position, die von Hand zu klaeren ist; in der anderen
 * kostet er den Beleg der Mutter.
 */
const entryTime = (entry: ServiceTransaction): number => {
    const ms = Date.parse(entry?.bookedAt || entry?.date || '');
    return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
};

/**
 * Plant, was beim TRENNEN mit den Positionen der Mutter geschieht.
 *
 * `parentLog` ist das Leistungsprotokoll der MUTTER und entscheidet ueber
 * `billed_at_parent`. Fehlt es, ist keine Abrechnung nachweisbar und es wird
 * auch keine unterstellt — der Aufrufer muss es mitgeben (der Dialog laedt das
 * Mutterprojekt ohnehin, um die Positionen ueberhaupt zu haben).
 *
 * Die Pauschalen-Deckung wird hier gestempelt (`stampFlatCoverage`) und nicht
 * vom Aufrufer erwartet: `flatCoveredBy` ist eine Ableitung ueber die VOLLE
 * Liste, und eine Regel, die auf einen Stempel angewiesen ist, den irgendwer
 * anders setzen muss, ist bei der ersten neuen Aufrufstelle still ausser
 * Kraft.
 */
export const planLinkReturn = (
    parentServices: ServiceItem[] | undefined | null,
    childProjectId: string | undefined | null,
    parentLog?: ServiceTransaction[] | null,
): LinkReturnPlan => {
    const parents = stampFlatCoverage(parentServices || []);
    const childId = (childProjectId || '').trim();
    const log = Array.isArray(parentLog) ? parentLog : [];

    // Abrechnungszeilen je Position EINMAL sammeln, statt je Position erneut
    // ueber das ganze Protokoll zu laufen — ein Mutterprotokoll hat leicht
    // vierstellig viele Zeilen.
    const belegeJePosition = new Map<string, ServiceTransaction[]>();
    for (const entry of log) {
        if (!entry || typeof entry.serviceId !== 'string' || !entry.serviceId) continue;
        if (!isBillingEntry(entry)) continue;
        const bisher = belegeJePosition.get(entry.serviceId);
        if (bisher) bisher.push(entry);
        else belegeJePosition.set(entry.serviceId, [entry]);
    }

    const moves: LinkReturnMove[] = [];
    const holds: LinkReturnHold[] = [];
    let unstamped = 0;

    for (const service of parents) {
        const origin = service.transferredFrom;
        if (!origin) { unstamped++; continue; }
        // Eine fremde Tochter geht diesen Vorgang nichts an — sie zaehlt auch
        // nicht als Altbestand, ihre Herkunft ist ja bekannt.
        if (!childId || origin.projectId !== childId) continue;

        // Ohne lesbaren Umzugszeitpunkt zaehlt JEDE Abrechnungszeile: dann ist
        // nicht zu trennen, was vor und was nach dem Umzug gefordert wurde.
        // Ein Zeitpunkt in der ZUKUNFT ist genauso unbrauchbar — er entsteht
        // nur durch eine verstellte Uhr beim Stempeln oder einen verbogenen
        // Wert, und er irrte in die FALSCHE Richtung: jede echte Abrechnung
        // laege "davor", und die Position ginge trotz Beleg zurueck. Beides
        // faellt deshalb auf dieselbe sperrende Seite.
        const umzug = Date.parse(origin.at || '');
        const seit = Number.isNaN(umzug) || umzug > Date.now()
            ? Number.NEGATIVE_INFINITY
            : umzug;
        const referenzen = (belegeJePosition.get(service.id) || [])
            .filter(entry => entryTime(entry) >= seit)
            .map(entry => (entry.bookingReference || '').trim())
            .filter(Boolean);

        if (referenzen.length > 0) {
            holds.push({
                service,
                origin,
                reason: 'billed_at_parent',
                bookingReferences: [...new Set(referenzen)],
            });
        } else if (service.flatRate) {
            holds.push({ service, origin, reason: 'flat_rate' });
        } else if (service.flatCoveredBy) {
            holds.push({ service, origin, reason: 'flat_covered' });
        } else {
            moves.push({ service, origin });
        }
    }

    // 'merged_untraceable' steht IMMER: ob beim Verknuepfen verschmolzen wurde,
    // laesst sich nicht feststellen — und eine Unsicherheit, die nur manchmal
    // genannt wird, liest sich beim naechsten Mal wie eine Entwarnung.
    const notes: LinkReturnNote[] = [];
    if (unstamped > 0) notes.push('unstamped_parent_services');
    notes.push('merged_untraceable');

    return { moves, holds, total: moves.length + holds.length, unstamped, notes };
};

/**
 * Das Leistungsprotokoll wandert MIT ZURUECK — dieselbe Mechanik wie in die
 * Hinrichtung, nur ohne Merge.
 *
 * Bewusst kein zweiter Rechenweg, sondern `planServiceLogTransfer` mit einem
 * Plan, der nur Moves kennt: die Faelle sind dieselben (eine Zeile ohne
 * `serviceId` bleibt liegen, die `id` des Eintrags wird nie angefasst, nichts
 * geht verloren und nichts steht doppelt), und zwei Fassungen derselben Regel
 * laufen frueher oder spaeter auseinander.
 *
 * Beim Zurueckholen gibt es kein Ziel umzuschreiben: jede Position behaelt ihre
 * `id` (sie zieht per `update` um), also wandern die Eintraege unveraendert.
 * Was zu einer ZURUECKGEHALTENEN Position gehoert, bleibt bei der Mutter — die
 * Zeile bleibt dort, ihr Nachweis auch.
 *
 * ─── `alreadyReturned`: die Reparatur nach einem TEILERFOLG ────────────────
 * Das Umhaengen der Positionen kann teilweise gelingen (RLS gibt nur einen
 * Teil frei, eine Zeile ist inzwischen weg). `applyLinkReturn` bricht dann VOR
 * dem Protokollteil ab — die bereits umgehaengten Positionen liegen bei der
 * Tochter, ihre Protokollzeilen aber weiter bei der Mutter. Dort sind sie
 * verwaist: der Leistungsnachweis der Mutter findet keine Position mehr dazu,
 * und der Projektcheck meldet die Menge an der Tochter als `unlogged_quantity`.
 *
 * Beim erneuten Trennen taucht so eine Position in KEINEM Plan mehr auf — sie
 * liegt ja schon bei der Tochter, `planLinkReturn` sieht nur noch die Zeilen
 * der Mutter. Ohne diesen Parameter bliebe ihre Protokollzeile fuer immer bei
 * der Mutter liegen. Uebergeben werden deshalb die ids der Positionen, die
 * TATSAECHLICH bei der Tochter liegen (aus der Datenbank, nicht aus einem
 * Plan): eine Protokollzeile der Mutter, die auf eine Position der Tochter
 * zeigt, gehoert an die Tochter, gleichgueltig wann sie dorthin geraten ist.
 *
 * Ids, die dem Plan ohnehin schon angehoeren, aendern nichts (die Zuordnung
 * ist eine Map je `serviceId`); die Angabe darf also grosszuegig sein.
 */
export const planLinkReturnLog = (
    plan: LinkReturnPlan,
    parentLog: ServiceTransaction[] | undefined | null,
    alreadyReturned?: readonly string[] | null,
): ServiceLogTransfer => {
    const zusaetzlich = (alreadyReturned || [])
        .filter((id): id is string => typeof id === 'string' && !!id)
        .map(id => ({ service: { id } as ServiceItem }));
    return planServiceLogTransfer(
        {
            moves: [...plan.moves.map(m => ({ service: m.service })), ...zusaetzlich],
            merges: [],
            total: plan.moves.length + zusaetzlich.length,
        },
        parentLog,
    );
};
